import {computed, Injectable, signal} from '@angular/core';
import {BehaviorSubject, Observable, Subject} from 'rxjs';
import {
  ChartInteractiveSort,
  ChartModel,
  ColumnData,
  DescriptorColumn, DrillingFilterValue,
  DrillThroughProperties,
  InteractionStatus,
  LegendColumn, NumberDisplayType,
  SecondValueColumn,
  ValueColumn,
} from '../models/chart';
import {ChartType} from '../models/chartType';
import {GridItem} from '../models/gridItem';
import {DashboardAndGroupInList} from '../models/dashboard-and-group-in-list';
import {Datasource} from '../models/datasource';
import {Filter} from '../models/filter';
import {Submit} from '../../shared/models/submit.enum';
import {Dashboard} from '../models/dashboard';
import {DataModuleType} from '../models/data-module-type.type';
import {DataType} from '../models/data-type.enum';
import {ChartFilterValue, ControllerValue} from '../models/controller-value.model';
import {Comment} from '../models/comment.model';
import {UserMention} from '../models/user-mention.model';
import {CommentState} from '../models/comment-state.type';
import {SubmitType} from '../../shared/models/submit.type';
import {CommentType} from '../models/comment.type';
import {GridsterItem} from 'angular-gridster2';
import { CalculateColumnModeType } from '../models/calculateColumnModeType';
import {CalculateColumnModel} from '../../data-source/models/calculate-column.model';
import {DashboardColumn} from '../models/dashboard-column';
import {ChartInteractionType} from '../models/chart-interaction.type';
import {InteractionValues} from '../models/interaction-values.model';
import {Controller} from '../models/controller.model';
import {skip} from 'rxjs/operators';
import {ColumnColor} from '../models/columnColor.model';
import {DashboardDrillThrough} from '../models/dashboard-drill-through';
import {DashboardControllersFilter} from '../models/dashboard-controllers-filter';
import {DrillThroughSourceDashboard} from '../models/drill-through-source-dashboard';
import {DrillThroughStateType} from '../models/drill-through-state.type';
import {RouteType, RoutingDrillThrough} from '../models/routingDrillThrough.model';
import {MapModel} from '../models/map.model';
import {ShareProperties} from '../models/share-properties.model';
import {UserFilter} from '../../shared/models/user-filter.model';
import {UserFilterDetails} from '../../shared/models/userFilterDetails.model';
import {UserInUserFilter} from '../../shared/models/userInUserfilter.model';
import {DrillThroughFiltersState} from '../models/drill-through-filters-state.type';
import {AdvanceSingleDate} from '../models/advance-single-date.model';
import {AdvanceRangeDate} from '../models/advance-range-date.model';
import { IExportChartData } from '../models/IExportChartData.model';
import {TemporaryDashboard} from '../models/temporary-dashboard.model';
import {DashboardType} from '../models/dashboard.type';
import { CalenderType } from '../models/calender.type';
import { DateListOptions } from '../models/default-value-date-list.type';
import * as _ from "lodash";
import {RelativeRangeDate} from "../models/relative-range-.model";
import { DashboardInfoDto } from '../models/dahsbaord-id.model';
import { ImportDatasourceState } from '../models/importDatasource.model';


@Injectable()
export class DashboardState {
  drillingStartAtLevel = 2;
  sourceDashboardControllers: Partial<Controller>[];
  selectedControllerValue: Partial<Controller>;
  previousSelectedControllerValue: Partial<Controller> = {};
  updateChartInteractAbilityStatus: boolean;
  drillThroughFiltersState: DrillThroughFiltersState = 'None';
  isDeletedController = false;
  hasChangedDesignSetting = false;
  hasChangedChartGrids = false;

  private editMode$ = new BehaviorSubject<boolean>(false);
  private dashboardUserFilterIsPermitted$ = new BehaviorSubject<boolean>(undefined);

  private dashboard$ = new BehaviorSubject<Dashboard>(null);
  private dashboardUpdating$ = new BehaviorSubject<boolean>(false);
  private dashboardReload$ = new BehaviorSubject<boolean>(false);
  private dashboardShareProperties$ = new BehaviorSubject<ShareProperties>(null);

  private assetsList$ = new BehaviorSubject<Partial<DashboardAndGroupInList>[]>([]);
  private dashboardListIsLoading$ = new BehaviorSubject<boolean>(false);
  private dashboardListUpdating$ = new BehaviorSubject<boolean>(false);
  private rollBackDashboardTemp: any;

  private dataSourcesOfDashboard$ = new BehaviorSubject<Partial<Datasource>[]>([]);
  private dataSourcesOfDashboardLoaded$ = new BehaviorSubject<boolean>(false);

  private parametersOfDashboard$ = new BehaviorSubject<Partial<Datasource>[]>([]);
  private parameterSubmitting$ = new BehaviorSubject<SubmitType>('notSubmitted');

  private selectedChart$ = new BehaviorSubject<Partial<ChartModel>>(null);
  private selectedChartFilters$ = new BehaviorSubject<Partial<Filter>[]>(undefined);
  private selectedFilterColumnValues$ = new BehaviorSubject<any []>(null);
   charts$ = new BehaviorSubject<Partial<ChartModel>[]>([]);
  private newChart$ = new BehaviorSubject<Partial<ChartModel>>(null);
  private rollbackChartDesignSetting$ = new BehaviorSubject<Partial<ChartModel>>(null);
  private rollbackChartAnnotationSetting$ = new BehaviorSubject<Partial<ChartModel>>(null);
  private minMaxColumnData$ = new BehaviorSubject<string[]>([]);

  private calculateColumnMode$ = new BehaviorSubject<CalculateColumnModeType>('None');
  private editCalculateColumn$ = new BehaviorSubject<Partial<CalculateColumnModel>>(null);
  private calculateColumnSubmit$ = new BehaviorSubject<SubmitType>('notSubmitted');
  private calculateColumnError$ =  new BehaviorSubject<string>(null);

  private dashboardSettingSidebarCompacted$ = new BehaviorSubject<boolean>(true);
  private dashboardSettingChangeSizeEvent$ = new Subject<void>();
  private settingSidebarId$ = new BehaviorSubject<ChartType>('dashboard');

  private dashboardFullSize$ = new BehaviorSubject<boolean>(false);
  private chartFullSize$ = new BehaviorSubject<boolean>(false);

  private dashboardFilters$ = new BehaviorSubject<Partial<Filter>[]>([]);
  private filterSubmitting$ = new BehaviorSubject<Submit>(Submit.notSubmitted);

  private controllers$ = new BehaviorSubject<Partial<Controller>[]>([]);
  private rollBackControllers: Partial<Controller>[] = null;

  private toggleDashboardComments$ = new BehaviorSubject<boolean>(false);
  private comments$ = new BehaviorSubject<Comment[]>(null);
  private usersToMention: UserMention[] = [];
  private commentState$ = new BehaviorSubject<CommentState>('None');
  private commentsType$ = new BehaviorSubject<CommentType>('All');

  private editInteractionMode$ = new BehaviorSubject<boolean>(false);
  private interactionMode$ = new BehaviorSubject<boolean>(false);
  private interactSourceChart$ = new BehaviorSubject<Partial<ChartModel>>(null);
  private selectedInteractionChart$ = new BehaviorSubject<Partial<ChartModel>>(null);
  private interactionValues$ = new BehaviorSubject<InteractionValues[]>([]);
  private interactionCharts$ = new BehaviorSubject<Partial<ChartModel>[]>(null);

  private isDrillThrough$ = new BehaviorSubject<boolean>(false);
  private drillThroughState$ = new BehaviorSubject<DrillThroughStateType>('None');
  private routingDrillThroughState$ =
    new BehaviorSubject<RoutingDrillThrough>({routeType: 'None',workspaceId: null,dashboardId: null});
  private drillThroughActivated$ = new BehaviorSubject<boolean>(false);
  private drillThroughChart$ = new BehaviorSubject<Partial<ChartModel>>(null);
  private drillThroughSourceDashboard$ = new BehaviorSubject<DrillThroughSourceDashboard>(null);
  private drillThroughFilters$ = new BehaviorSubject<DashboardControllersFilter[]>([]);
  private drillThroughChartFilters$ = new BehaviorSubject<any[]>([]);
  private chartFilters$ = new BehaviorSubject<any[]>([]);
  private dashboardsForDrillThrough$ = new BehaviorSubject<DashboardDrillThrough[]>([]);
  private dashboardUsedInDrillThrough$ = new BehaviorSubject<DashboardDrillThrough>(null);
  private chartsDrillDownValues$ = new BehaviorSubject<DrillingFilterValue[][]>([]);
  private chartsDrillDownMode$ = new BehaviorSubject<boolean[]>(null);
  private controllersFilters$ = new BehaviorSubject<DashboardControllersFilter[]>([]);

  // user filter
  private dashboardUserFilters$ = new BehaviorSubject<string[]>([]);
  private userFiltersListAddToDashboard$ = new BehaviorSubject<Partial<UserFilter>[]>([]);
  private chartsUserFilters$ = new BehaviorSubject<Array<string[]>>([]);
  private selectedChartUserFilters$ = new BehaviorSubject<string[]>(undefined);
  private userFiltersListAddToCharts$ = new BehaviorSubject<Array<Partial<UserFilter>[]>>([]);
  private selectedUserFiltersListAddToChart$ = new BehaviorSubject<Partial<UserFilter>[]>([]);
  private userFilterDetails$ = new BehaviorSubject<UserFilterDetails>(null);
  private selectedUsersInUserFilter$ = new BehaviorSubject<Partial<UserInUserFilter>[]>([]);

  // save dashboard
  private temporaryDashboard$ = new BehaviorSubject<TemporaryDashboard>(null);
  private shouldSaveDashboard$ = new BehaviorSubject<boolean>(false);
  private dashboardType$ = new BehaviorSubject<DashboardType>('None');

  private previousSelectedChartColors: {colors: any; columnDataIndex: number};

  private mapsList$ = new BehaviorSubject<Partial<MapModel[]>>(undefined);

  private mapKeys$ = new BehaviorSubject<any>(undefined);

  private chartsHasUserSort$ = new BehaviorSubject<Partial<ChartModel>[]>([]);

  private submitState$ = new BehaviorSubject<SubmitType>('notSubmitted');
  private dashboardChartNameSubmitState$ = new BehaviorSubject<SubmitType>('notSubmitted');

  private exportChartDataDetail$ = new BehaviorSubject<IExportChartData>(null);
  private compactedDashboardListStatus$ = new BehaviorSubject<boolean>(false);
  private dashboardsListResize$ = new BehaviorSubject<boolean>(false);
  private chartsZoomStatus$ = new BehaviorSubject<boolean[]>([]);
  private defaultDataSource$ = new BehaviorSubject<Partial<Datasource>>(null);
  private defaultDataSourceDate$ = new BehaviorSubject<number>(null);
  private gridMigrationState$ = new BehaviorSubject<boolean>(false);
  private dashboardInfo$ = new BehaviorSubject<DashboardInfoDto | null>(null);
  private chartInsight$ = new BehaviorSubject<any>(null);
  dashboardHistoryCurrent = signal<any | null>(null);
  dashboardHistoryList   = signal<any[]>([]);
  dashboardHistoryLoading = signal<boolean>(false);
  isDashboardInHistoryMode = signal<boolean>(false);
  dashboardRecoveryCurrent = signal<any | null>(null);
  dashboardRecoveryLoading = signal<boolean>(false);
  dashboardRecoverySuccess = signal<boolean>(false);
  dashboardPinCurrent = signal<any | null>(null);
  dashboardPinLoading = signal<boolean>(false);
  dashboardNameDuplicated = signal<boolean | null>(false);
  chartNameDuplicated = signal<boolean>(false);
  nameSubmitState  = signal<SubmitType>("notSubmitted");

  defaultDatasourceByChart = signal<Record<string, {
    list: any[];
    selectedId: string | null;
    hasDefault: boolean;
    loading: boolean;
    tickTime: string | null;
  }>>({});

  isCopyChartInProgress = signal<boolean>(false);
  isAddNewChartInProgress = signal<boolean>(false);
  paintFormatState = signal<any|undefined>(undefined);
  advancedCardGridState = signal<string>(undefined);

  exportDashboardLoading = signal<boolean>(false);

  importDashboardId = signal<string | null>(null);
  importDatasources = signal<ImportDatasourceState[]>([]);
  importStepIndex = signal<number>(0);
  importLoading = signal<boolean>(false);

  importFinalizeLoading = signal<boolean>(false);

importCurrentDatasource = computed(() => {
  const index = this.importStepIndex();
  const datasources = this.importDatasources();
  return index < datasources.length ? datasources[index] : null;
});


  importTotalSteps = computed(() =>
    this.importDatasources().length + 1
  );

  importIsLastStep = computed(() =>
    this.importStepIndex() === this.importDatasources().length
  );

  get dashboardId(): string {
    return this.dashboard$.getValue()?.dashboardId;
  }

  get workspaceId(): string {
    return this.dashboard$.getValue().workspaceId;
  }

  setGridMigrated(isMigrated: boolean): void {
    this.gridMigrationState$.next(isMigrated);
  }

  getGridMigrationState$(): Observable<boolean> {
    return this.gridMigrationState$.asObservable();
  }

  setSubmitState(state: SubmitType): void {
    this.submitState$.next(state);
  }

  getSubmitState$(): Observable<SubmitType> {
    return this.submitState$.asObservable();
  }
  setDashboardChartNameSubmitState(state: SubmitType): void {
    this.dashboardChartNameSubmitState$.next(state);
  }

  getDashboardChartNameSubmitState$(): Observable<SubmitType> {
    return this.dashboardChartNameSubmitState$.asObservable();
  }
  isEditMode$(): Observable<boolean> {
    return this.editMode$.asObservable();
  }

  getIsEditModeValue(): boolean {
    return this.editMode$.getValue();
  }

  toggleEditMode(): void {
    this.editMode$.next(!this.editMode$.getValue());
    this.dashboardSettingSidebarToggleCompact(!this.editMode$.getValue());
    if(this.editMode$.getValue()){
      this.setSelectedChart(undefined);
      this.setSettingSidebarId('dashboard');
    }else{
      this.setCalculateColumnMode('None');
    }
  }

  setEditMode(isEditMode: boolean): void {
    this.editMode$.next(isEditMode);
    this.dashboardSettingSidebarToggleCompact(isEditMode);
  }

  getDashboardReload$(): Observable<boolean>{
    return this.dashboardReload$.asObservable();
  }
  reloadDashboard(): void {
    this.dashboardReload$.next(true);
  }

  //dashboard list
  getAssetsList$(): Observable<Partial<DashboardAndGroupInList>[]> {
    return this.assetsList$.asObservable();
  }

  updateAssetList(assetsList: Partial<DashboardAndGroupInList>[], selectedDashboardId: string): void {
    let dashboardFounded = false;
    assetsList.forEach(asset => {
      if(asset.assetsType === 'DashboardGroup') {
        asset.drop = false;
        const dashboards = asset.dashboards;
        if(!dashboardFounded) {
          for(let index = 0 ; index < dashboards.length; index++) {
            if(dashboards[index].dashboardId === selectedDashboardId) {
              asset.drop = true;
              dashboardFounded = true;
              break;
            }
          }
        }
      }
    });
    this.assetsList$.next(assetsList);
  }

  updateDashboardNameInList(dashboard: Dashboard): void {
    const assetsList = this.assetsList$.getValue();
    let isExistInDashboards = false;
    for(let index = 0 ; index < assetsList.length ; index ++) {
      if(assetsList[index].dashboardId === dashboard.dashboardId) {
        assetsList[index].name = dashboard.name;
        this.updateAssetList([...assetsList], this.dashboardId);
        isExistInDashboards = true;
        break;
      }
    }
    if(!isExistInDashboards) {
      let isFounded = false;
      for(let groupIndex = 0 ; groupIndex < assetsList.length ; groupIndex ++) {
        if(assetsList[groupIndex].assetsType !== 'DashboardGroup') {
          continue;
        }
        const dashboards = assetsList[groupIndex].dashboards;
        for(let dashboardIndex = 0 ; dashboardIndex < dashboards.length ; dashboardIndex++) {
          if(dashboards[dashboardIndex].dashboardId === dashboard.dashboardId) {
            dashboards[dashboardIndex].name = dashboard.name;
            assetsList[groupIndex].dashboards = [...dashboards];
            this.updateAssetList([...assetsList], this.dashboardId);
            isFounded = true;
            break;
          }
        }
        if(isFounded) {
          break;
        }
      }
    }
  }
  dashboardListIsUpdating$(): Observable<boolean> {
    return this.dashboardListUpdating$.asObservable();
  }
  dashboardListSetUpdating(isUpdating: boolean): void {
    this.dashboardListUpdating$.next(isUpdating);
  }

  getDashboardListIsLoading$(): Observable<boolean> {
    return this.dashboardListIsLoading$.asObservable();
  }
  setDashboardListIsLoading(isUpdating: boolean): void {
    this.dashboardListIsLoading$.next(isUpdating);
  }
  //dashboard
  getDashboard$(): Observable<any> {
    return this.dashboard$.asObservable();
  }
  getDashboardValue(): Dashboard {
    return this.dashboard$.getValue();
  }

  getRollBackDashboardTemp(){
    return this.rollBackDashboardTemp;
  }

  updateDashboard(dashboard: any): void {
    this.rollBackDashboardTemp = this.dashboard$.getValue();
    dashboard.chartsGridViewPermitted =
      this.checkDashboardChartsGridIsPermitted(dashboard.charts, dashboard.chartsGrid.grid);
    dashboard.charts = this.indexingChartsInDashboard(dashboard.charts);
    dashboard.charts = this.updateDashboardChartsNumberDisplayTypes(dashboard.charts,
        dashboard.dashboardDesignSettings.numberDisplayType);
    this.updateCharts(dashboard.charts);
    this.dashboard$.next(dashboard);
  }

  /** setter */

  checkDashboardChartsGridIsPermitted(charts: Partial<ChartModel>[],chartsGrid: GridsterItem[]): GridsterItem[] {
    const result: GridsterItem[]= [];
    if(chartsGrid) {
      chartsGrid.forEach((grid,index) => {
        for(const chart of charts) {
          if(chart.chartId === grid.id) {
            result.push(grid);
            break;
          }
        }
      });
    }
    return result;
  }

  indexingChartsInDashboard(charts: Partial<ChartModel>[]): Partial<ChartModel>[] {
    if(!Array.isArray(charts)) {
      return [];
    }
    charts.forEach((chart,index) => charts[index].chartIndexInDashboard = index);
    const chartsDrillDownValuesLength = this.chartsDrillDownValues$.getValue().length;
    if(chartsDrillDownValuesLength === 0 && charts.length > 0) {
      this.setDefaultChartsDrillDownValuesState(charts.length);
    }
    return charts;
  }

  rollBackDashboard(){
    this.dashboard$.next(this.rollBackDashboardTemp);
  }
  dashboardIsUpdating$(): Observable<boolean> {
    return this.dashboardUpdating$.asObservable();
  }
  dashboardSetUpdating(isUpdating: boolean) {
    this.dashboardUpdating$.next(isUpdating);
  }

  //save dashboard
  setDashboardTypeState(dashboardType: DashboardType): void {
    this.dashboardType$.next(dashboardType);
  }

  getDashboardTypeState$(): Observable<DashboardType> {
    return this.dashboardType$.asObservable();
  }

  setTemporaryDashboard(temporaryDashboard: TemporaryDashboard): void {
    this.temporaryDashboard$.next(temporaryDashboard);
  }

  resetDashboardState(): void{
    this.dashboard$.next(null);
  }

  getTemporaryDashboard$(): Observable<TemporaryDashboard> {
    return this.temporaryDashboard$.asObservable();
  }

  getMainDashboardId(): string {
    return this.temporaryDashboard$.getValue()?.mainDashboardId;
  }

  getTemporaryDashboardValue(): TemporaryDashboard {
    return this.temporaryDashboard$.getValue();
  }

  setShouldSaveDashboard(shouldSave: boolean): void {
    this.shouldSaveDashboard$.next(shouldSave);
  }

  updateShouldSaveDashboard(): void {
    if(!this.getShouldSaveDashboardValue()) {
      this.setShouldSaveDashboard(true);
    }
  }

  getShouldSaveDashboard$(): Observable<boolean> {
    return this.shouldSaveDashboard$.asObservable();
  }

  getShouldSaveDashboardValue(): boolean {
    return this.shouldSaveDashboard$.getValue();
  }

  //parameter
  getParametersOfDashboard$(): Observable<Partial<Datasource>[]> {
    return this.parametersOfDashboard$.asObservable();
  }

  updateParametersOfDashboard(parameters: Partial<Datasource>[]): void {
    this.parametersOfDashboard$.next(parameters);
  }
  updateChartName(chart: Partial<ChartModel>){
    const charts = this.charts$.getValue();
    const index = charts.findIndex(chartI => chartI.chartId === chart.chartId);
    charts[index] = {...chart};
    this.setSelectedChart({...chart});
    this.updateCharts([...charts]);
  }
  updateSingleParameter(parameter: Partial<Datasource>): void{
    const currentParameters = this.parametersOfDashboard$.getValue();
    const parameterIndex = currentParameters.findIndex(d => d.dataSourceId === parameter.dataSourceId);
    parameter.name = parameter.parameterProperties.parameterName;
    if(parameterIndex > -1) {
      currentParameters[parameterIndex] = parameter;
      this.parametersOfDashboard$.next([...currentParameters]);
    }
  }

  deleteParameter(dataSourceId: string): void {
    const currentParameters = this.parametersOfDashboard$.getValue();
    const parameterIndex =  currentParameters.findIndex(datasource => datasource.dataSourceId === dataSourceId);
    if(parameterIndex > -1) {
      currentParameters.splice(parameterIndex,1);
      this.parametersOfDashboard$.next([...currentParameters]);
    }
  }

  updateParameterSubmit(string: SubmitType): void {
    this.parameterSubmitting$.next(string);
  }
  getParameterSubmit$(): Observable<SubmitType> {
    return this.parameterSubmitting$.asObservable();
  }

  //data sources
  getDataSourcesOfDashboard$(): Observable<Partial<Datasource>[]> {
    return this.dataSourcesOfDashboard$.asObservable();
  }
  deleteDataSourceFromDashboard(dataSourceId: string): void {
    const currentDataSources = this.dataSourcesOfDashboard$.getValue();
    const dataSourceIndex = currentDataSources.findIndex(dataSource => dataSource.dataSourceId === dataSourceId);
    if(dataSourceIndex > -1) {
      currentDataSources.splice(dataSourceIndex, 1);
      this.dataSourcesOfDashboard$.next(currentDataSources);
    }
  }
  deleteDataSourceColumnFromDashboard(dataSourceId: string, columnId: string): void {
    const currentDataSources = this.dataSourcesOfDashboard$.getValue();
    const dataSource = currentDataSources.find(datasource => datasource.dataSourceId === dataSourceId);
    if(dataSource) {
      const columnIndex = dataSource.dashboardColumns.map(col => col.columnId).indexOf(columnId);
      if(columnIndex > -1) {
        dataSource.dashboardColumns.splice(columnIndex,1);
        this.dataSourcesOfDashboard$.next(currentDataSources);
      }
    }
  }

  updateDataSourcesOfDashboard(dataSources: Partial<Datasource>[]): void {
    const actualDataSources: Partial<Datasource>[] = [];
    const actualParameters: Partial<Datasource>[] = [];
    dataSources.forEach((dataSource) => {
     dataSource.dashboardColumns.forEach(column => column.columnIsMissed = false);
      if(dataSource.dataSourceType === 'Parameter') {
        actualParameters.push(dataSource);
      }else{
        actualDataSources.push(dataSource);
      }
    });
    this.dataSourcesOfDashboard$.next(actualDataSources);
    this.parametersOfDashboard$.next(actualParameters);
  }

  addParametersToDashboard(parameter: Partial<Datasource>) {
    const parameters = this.parametersOfDashboard$.getValue();
    parameters.push(parameter);
    this.parametersOfDashboard$.next([...parameters]);
  }

  setCustomNameForDataSourceColumnInDashboard(columnId: string,customName: string, dataSourceId: string): void {
    const currentDataSources = this.dataSourcesOfDashboard$.getValue();
    const dataSourceIndex = currentDataSources.findIndex(datasource => datasource.dataSourceId === dataSourceId);
    if(dataSourceIndex > -1) {
      const columnIndex = currentDataSources[dataSourceIndex]
          .dashboardColumns.findIndex(col => col.columnId === columnId);
      if(columnIndex > -1) {
        currentDataSources[dataSourceIndex].dashboardColumns[columnIndex].name = customName;
        currentDataSources[dataSourceIndex] = {...currentDataSources[dataSourceIndex]};
        this.dataSourcesOfDashboard$.next(currentDataSources);
      }
    }
  }
  updateDataSourceColumnInDashboard(column: Partial<DashboardColumn>, dataSourceId: string){
    const dataSources = this.dataSourcesOfDashboard$.getValue();
    const dataSourceIndex = dataSources
      .findIndex((dataSource) => (dataSource.dataSourceId === dataSourceId));
    if(dataSourceIndex > -1) {
      const columnIndex = dataSources[dataSourceIndex].dashboardColumns.
      findIndex((c) => c.columnId === column.columnId);
      if(columnIndex > -1) {
        dataSources[dataSourceIndex].dashboardColumns[columnIndex] = column as DashboardColumn;
        this.dataSourcesOfDashboard$.next(dataSources);
      }
    }

  }

  getDataSourcesLoaded$(): Observable<boolean> {
    return this.dataSourcesOfDashboardLoaded$.asObservable();
  }
  updateDataSourcesLoaded(newState: boolean): void {
    this.dataSourcesOfDashboardLoaded$.next(newState);
  }
  getColumnDataModuleType(columnId: string, dataSourceId: string): DataModuleType {
    const dataSource = this.dataSourcesOfDashboard$.getValue()
      .find(datasource => datasource.dataSourceId === dataSourceId);
    let dataModuleType;
    if(dataSource) {
      dataModuleType = dataSource.dashboardColumns.find(column => column.columnId === columnId)?.dataModuleType;
    } else {
      const parameter = this.parametersOfDashboard$.getValue()
        .find(parameterTemp => parameterTemp.dataSourceId === dataSourceId);
      if(parameter) {
        dataModuleType = parameter.dashboardColumns.find(column => column.columnId === columnId)?.dataModuleType;
      }
    }
    return dataModuleType;
  }
  getColumnDataType(columnId: string, dataSourceId: string): DataType {
    const dataSource = this.dataSourcesOfDashboard$.getValue()
      .find(datasource => datasource.dataSourceId === dataSourceId);
    let dataType;
    if(dataSource) {
      dataType = dataSource.dashboardColumns.find(column => column.columnId === columnId)?.dataType;
    } else {
      const parameter = this.parametersOfDashboard$.getValue()
        .find(parameterTemp => parameterTemp.dataSourceId === dataSourceId);
      if(parameter) {
        dataType = parameter.dashboardColumns.find(column => column.columnId === columnId)?.dataType;
      }
    }
    return dataType;
  }

  columnIsMissed(dataSourceId: string, columnId: string): boolean {
    const dataSources = this.dataSourcesOfDashboard$.getValue();
    const parameters = this.parametersOfDashboard$.getValue();
    const dataSourceIndex = dataSources.findIndex(datasource => datasource.dataSourceId === dataSourceId);
    const parameterIndex = parameters.findIndex(tempParameter => tempParameter.dataSourceId === dataSourceId);
    if(dataSourceIndex < 0 && parameterIndex < 0) {
      return true;
    }

    const column = dataSourceIndex > -1 ?
        dataSources[dataSourceIndex].dashboardColumns.find(tempColumn => tempColumn.columnId === columnId) :
        parameters[parameterIndex].dashboardColumns.find(tempColumn => tempColumn.columnId === columnId);
    return column == null;
  }
  getColumnName(columnId: string, dataSourceId: string): string {
    const dataSource = this.dataSourcesOfDashboard$.getValue()
      .find(datasource => datasource.dataSourceId === dataSourceId);
    let columnName;
    if(dataSource) {
      columnName = dataSource.dashboardColumns.find(column => column.columnId === columnId)?.name;
    } else {
      const parameter = this.parametersOfDashboard$.getValue()
        .find(parameterTemp => parameterTemp.dataSourceId === dataSourceId);
      if(parameter) {
        columnName = parameter.dashboardColumns.find(column => column.columnId === columnId)?.name;
      }
    }
    return columnName;
  }
  updateDescriptorColumn(chartId: string, id: string, newColumn: Partial<DescriptorColumn>){
    const charts = this.charts$.getValue();
    let chart = charts.find(chartTemp => chartTemp.chartId === chartId);
    if(chart) {
      const descriptorColumnIndex = chart.chartDataSettings.descriptorColumns
          .findIndex(descriptorColumn => descriptorColumn.id === id);
      if(descriptorColumnIndex > -1) {
        chart.chartDataSettings.descriptorColumns[descriptorColumnIndex] = newColumn;
        this.updateNameInChartFilters(chart, newColumn.columnId, newColumn.name);
        chart = {...chart};
        this.updateCharts(charts);
        this.selectedChart$.next(chart);
        this.setControllersFilters();
      }
    }
  }
  updateLegendColumn(chartId: string, id: string, newColumn: Partial<LegendColumn>){
    const charts = this.charts$.getValue();
    let chart = charts.find(chartTemp => chartTemp.chartId === chartId);
    if(chart) {
      const legendColumnIndex = chart.chartDataSettings.legendColumns
          .findIndex(legendColumn => legendColumn.id === id);
      if(legendColumnIndex > -1) {
        chart.chartDataSettings.legendColumns[legendColumnIndex] = newColumn;
        this.updateNameInChartFilters(chart, newColumn.columnId, newColumn.name);
        chart = {...chart};
        this.updateCharts(charts);
        this.selectedChart$.next(chart);
      }
    }
  }

  updateValueColumn(chartId: string, id: string, newColumn: Partial<ValueColumn>){
    const charts = this.charts$.getValue();
    let chart = charts.find(chartTemp => chartTemp.chartId === chartId);
    if(chart) {
      const valueColumnIndex = chart.chartDataSettings.valueColumns
          .findIndex(valueColumn => valueColumn.id === id);
      if(valueColumnIndex > -1) {
        chart.chartDataSettings.valueColumns[valueColumnIndex] = newColumn;
        this.updateNameInChartFilters(chart, newColumn.columnId, newColumn.name);
        chart = {...chart};
        this.updateCharts(charts);
        this.selectedChart$.next(chart);
      }
    }
  }

  updatSortColumn(chartId: string, id: string, newColumn: Partial<ChartInteractiveSort>){
    const charts = this.charts$.getValue();
    let chart = charts.find(chartTemp => chartTemp.chartId === chartId);
    if(chart) {
      const sortColumnIndex = chart.sortChartColumns
          .findIndex(sortColumn => sortColumn.id === id);
      if(sortColumnIndex > -1) {
        chart.sortChartColumns[sortColumnIndex] = newColumn;
        this.updateNameInChartFilters(chart, newColumn.columnId, newColumn.name);
        chart = {...chart};
        this.updateCharts(charts);
        this.selectedChart$.next(chart);
      }
    }
  }

  updateSourceNameColumn(chartId: string, id: string, newColumn: Partial<ValueColumn>){
    const charts = this.charts$.getValue();
    let chart = charts.find(chartTemp => chartTemp.chartId === chartId);
    if(chart) {
      const sourceNameColumnIndex = chart.chartDataSettings.sourceNameColumns
        .findIndex(sourceNameColumn => sourceNameColumn.id === id);
      if(sourceNameColumnIndex > -1) {
        chart.chartDataSettings.sourceNameColumns[sourceNameColumnIndex] = newColumn;
        this.updateNameInChartFilters(chart, newColumn.columnId, newColumn.name);
        chart = {...chart};
        this.updateCharts(charts);
        this.selectedChart$.next(chart);
      }
    }
  }

  updateTargetNameColumn(chartId: string, id: string, newColumn: Partial<ValueColumn>){
    const charts = this.charts$.getValue();
    let chart = charts.find(chartTemp => chartTemp.chartId === chartId);
    if(chart) {
      const targetNameColumnIndex = chart.chartDataSettings.targetNameColumns
        .findIndex(targetNameColumn => targetNameColumn.id === id);
      if(targetNameColumnIndex > -1) {
        chart.chartDataSettings.targetNameColumns[targetNameColumnIndex] = newColumn;
        this.updateNameInChartFilters(chart, newColumn.columnId, newColumn.name);
        chart = {...chart};
        this.updateCharts(charts);
        this.selectedChart$.next(chart);
      }
    }
  }

  updateSourceGroupColumn(chartId: string, id: string, newColumn: Partial<ValueColumn>){
    const charts = this.charts$.getValue();
    let chart = charts.find(chartTemp => chartTemp.chartId === chartId);
    if(chart) {
      const sourceGroupColumnIndex = chart.chartDataSettings.sourceGroupColumns
        .findIndex(sourceGroupColumn => sourceGroupColumn.id === id);
      if(sourceGroupColumnIndex > -1) {
        chart.chartDataSettings.sourceGroupColumns[sourceGroupColumnIndex] = newColumn;
        this.updateNameInChartFilters(chart, newColumn.columnId, newColumn.name);
        chart = {...chart};
        this.updateCharts(charts);
        this.selectedChart$.next(chart);
      }
    }
  }

  updateTargetGroupColumn(chartId: string, id: string, newColumn: Partial<ValueColumn>){
    const charts = this.charts$.getValue();
    let chart = charts.find(chartTemp => chartTemp.chartId === chartId);
    if(chart) {
      const targetGroupColumnIndex = chart.chartDataSettings.targetGroupColumns
        .findIndex(sourceGroupColumn => sourceGroupColumn.id === id);
      if(targetGroupColumnIndex > -1) {
        chart.chartDataSettings.targetGroupColumns[targetGroupColumnIndex] = newColumn;
        this.updateNameInChartFilters(chart, newColumn.columnId, newColumn.name);
        chart = {...chart};
        this.updateCharts(charts);
        this.selectedChart$.next(chart);
      }
    }
  }

  updateTargetGroupNameColumn(chartId: string, id: string, newColumn: Partial<ValueColumn>){
    const charts = this.charts$.getValue();
    let chart = charts.find(chartTemp => chartTemp.chartId === chartId);
    if(chart) {
      const targetGroupNameColumnIndex = chart.chartDataSettings.targetGroupNameColumns
        .findIndex(sourceGroupColumn => sourceGroupColumn.id === id);
      if(targetGroupNameColumnIndex > -1) {
        chart.chartDataSettings.targetGroupNameColumns[targetGroupNameColumnIndex] = newColumn;
        this.updateNameInChartFilters(chart, newColumn.columnId, newColumn.name);
        chart = {...chart};
        this.updateCharts(charts);
        this.selectedChart$.next(chart);
      }
    }
  }

  updateSecondValueColumn(chartId: string, id: string, newColumn: Partial<ValueColumn>){
    const charts = this.charts$.getValue();
    let chart = charts.find(chartTemp => chartTemp.chartId === chartId);
    if(chart) {
      const valueColumnIndex = chart.chartDataSettings.secondValueColumns
          .findIndex(valueColumn => valueColumn.id === id);
      if(valueColumnIndex > -1) {
        chart.chartDataSettings.secondValueColumns[valueColumnIndex] = newColumn;
        this.updateNameInChartFilters(chart, newColumn.columnId, newColumn.name);
        chart = {...chart};
        this.updateCharts(charts);
        this.selectedChart$.next(chart);
      }
    }
  }
  updateNameInChartFilters(chart: Partial<ChartModel>, columnId: string, name: string): void {
    const selectedChartFilters = this.selectedChartFilters$?.getValue();
    const filterIndex = selectedChartFilters?.findIndex(filter => filter.columnId === columnId);
    if(filterIndex > -1) {
      selectedChartFilters[filterIndex].columnName = name;
      this.selectedChartFilters$.next([...selectedChartFilters]);
    }
  }

  deleteDescriptorColumn(chartId: string, descriptorId: string){
    const charts = this.charts$.getValue();
    let chart = charts.find(chartTemp => chartTemp.chartId === chartId);
    if(chart) {
      const descriptorColumnIndex = chart.chartDataSettings.descriptorColumns
          .findIndex(descriptorColumn => descriptorColumn.id === descriptorId);
      if(descriptorColumnIndex > -1) {
        chart.chartDataSettings.descriptorColumns.splice(descriptorColumnIndex, 1);
        chart = this.resetChartDrillingProperties(chart);
        this.updateOneChart(chart);
      }
    }
  }

  deleteLegendColumn(chartId: string, legendId: string): void {
    const charts = this.charts$.getValue();
    const chart = charts.find(chartTemp => chartTemp.chartId === chartId);
    if(chart) {
      const legendColumnIndex = chart.chartDataSettings.legendColumns
          .findIndex(valueColumn => valueColumn.id === legendId);
      if(legendColumnIndex > -1) {
        chart.chartDataSettings.legendColumns.splice(legendColumnIndex, 1);
      }
    }
  }
  deleteValueColumn(chartId: string, valueId: string){
    const charts = this.charts$.getValue();
    const chart = charts.find(chartTemp => chartTemp.chartId === chartId);
    if(chart) {
      const valueColumnIndex = chart.chartDataSettings.valueColumns
          .findIndex(valueColumn => valueColumn.id === valueId);
      if(valueColumnIndex > -1) {
        chart.chartDataSettings.valueColumns.splice(valueColumnIndex, 1);
        chart.chartDataSettings.valueColumns.forEach((value, index) => value.priority = index + 1);
      }
    }
  }
  deleteSecondValueColumn(chartId: string, valueId: string){
    const charts = this.charts$.getValue();
    const chart = charts.find(chartTemp => chartTemp.chartId === chartId);
    if(chart) {
      const secondValueColumnIndex = chart.chartDataSettings.secondValueColumns
          .findIndex(secondValueColumn => secondValueColumn.id === valueId);
      if(secondValueColumnIndex > -1) {
        chart.chartDataSettings.secondValueColumns.splice(secondValueColumnIndex, 1);
        chart.chartDataSettings.secondValueColumns.forEach((value, index) => value.priority = index + 1);
      }
    }
  }

  addDescriptorColumn(descriptorColumn: Partial<DescriptorColumn>, chartId: string,index: number) {
    const chart = this.charts$.getValue().find(chartTemp => chartTemp.chartId === chartId);
    if(chart && index <= chart.chartDataSettings.descriptorColumns.length) {
      chart.chartDataSettings.descriptorColumns.splice(index, 0, descriptorColumn);
      chart.chartDataSettings.descriptorColumns.forEach((value, indexTemp) => value.priority = indexTemp + 1);
      if (this.selectedChart$.getValue()?.chartId === chart.chartId) {
        this.selectedChart$.next(chart);
      }
    }
  }

  addLegendColumn(legendColumn: Partial<LegendColumn>, chartId: string) {
    const chart = this.charts$.getValue().find(chartTemp => chartTemp.chartId === chartId);
    if(chart) {
      chart.chartDataSettings.legendColumns.push(legendColumn);
      if (this.selectedChart$.getValue()?.chartId === chart.chartId) {
        this.selectedChart$.next(chart);
      }
    }
  }

  addValueColumn(valueColumn: Partial<ValueColumn>, chartId: string, index: number) {
    const chart = this.charts$.getValue().find(chartTemp => chartTemp.chartId === chartId);
    if(chart && index <= chart.chartDataSettings.valueColumns.length) {
      chart.chartDataSettings.valueColumns.splice(index, 0, valueColumn);
      chart.chartDataSettings.valueColumns.forEach((value, indexTemp) => value.priority = indexTemp + 1);
      if (this.selectedChart$.getValue()?.chartId === chart.chartId) {
        this.selectedChart$.next(chart);
      }
    }
  }

  addSecondValueColumn(valueColumn: Partial<ValueColumn>, chartId: string, index: number) {
    const chart = this.charts$.getValue().find(chartTemp => chartTemp.chartId === chartId);
    if(chart && index <= chart.chartDataSettings.secondValueColumns.length) {
      chart.chartDataSettings.secondValueColumns.splice(index, 0, valueColumn);
      chart.chartDataSettings.secondValueColumns.forEach((value, indexTemp) => value.priority = indexTemp + 1);
      if (this.selectedChart$.getValue()?.chartId === chart.chartId) {
        this.selectedChart$.next(chart);
      }
    }
  }

  addChartSort(sortColumn: Partial<ChartInteractiveSort>, chartId: string, index: number) {
    const chart = this.charts$.getValue().find(chartTemp => chartTemp.chartId === chartId);
    if(!chart.sortChartColumns){
      chart.sortChartColumns = []
    }
    if(chart && index <= chart.sortChartColumns?.length) {
      chart.sortChartColumns.splice(index, 0, sortColumn);
      chart.sortChartColumns.forEach((value, indexTemp) => value.priority = indexTemp + 1);
      if (this.selectedChart$.getValue()?.chartId === chart.chartId) {
        this.selectedChart$.next(chart);
      }
    }
  }

  addSourceNameColumn(sourceNameColumn: Partial<ValueColumn>, chartId: string,index: number) {
    const chart = this.charts$.getValue().find(chartTemp => chartTemp.chartId === chartId);
    if(chart && index <= chart.chartDataSettings.sourceNameColumns.length) {
      chart.chartDataSettings.sourceNameColumns.splice(index, 0, sourceNameColumn);
      if (this.selectedChart$.getValue()?.chartId === chart.chartId) {
        this.selectedChart$.next(chart);
      }
    }
  }

  deleteSourceNameColumn(chartId: string, descriptorId: string){
    const charts = this.charts$.getValue();
    const chart = charts.find(chartTemp => chartTemp.chartId === chartId);
    if(chart) {
      const sourceNameColumnIndex = chart.chartDataSettings.sourceNameColumns
        .findIndex(descriptorColumn => descriptorColumn.id === descriptorId);
      if(sourceNameColumnIndex > -1) {
        chart.chartDataSettings.sourceNameColumns.splice(sourceNameColumnIndex, 1);
        this.updateOneChart(chart);
      }
    }
  }

  addTargetNameColumn(targetNameColumn: Partial<ValueColumn>, chartId: string,index: number) {
    const chart = this.charts$.getValue().find(chartTemp => chartTemp.chartId === chartId);
    if(chart && index <= chart.chartDataSettings.targetNameColumns.length) {
      chart.chartDataSettings.targetNameColumns.splice(index, 0, targetNameColumn);
      if (this.selectedChart$.getValue()?.chartId === chart.chartId) {
        this.selectedChart$.next(chart);
      }
    }
  }

  deleteTargetNameColumn(chartId: string, descriptorId: string){
    const charts = this.charts$.getValue();
    const chart = charts.find(chartTemp => chartTemp.chartId === chartId);
    if(chart) {
      const targetNameColumnIndex = chart.chartDataSettings.targetNameColumns
        .findIndex(targetNameColumn => targetNameColumn.id === descriptorId);
      if(targetNameColumnIndex > -1) {
        chart.chartDataSettings.targetNameColumns.splice(targetNameColumnIndex, 1);
        this.updateOneChart(chart);
      }
    }
  }

  addSourceGroupColumn(sourceGroupColumn: Partial<ValueColumn>, chartId: string,index: number) {
    const chart = this.charts$.getValue().find(chartTemp => chartTemp.chartId === chartId);
    if(chart && index <= chart.chartDataSettings.sourceGroupColumns.length) {
      chart.chartDataSettings.sourceGroupColumns.splice(index, 0, sourceGroupColumn);
      if (this.selectedChart$.getValue()?.chartId === chart.chartId) {
        this.selectedChart$.next(chart);
      }
    }
  }

  deleteSourceGroupColumn(chartId: string, descriptorId: string){
    const charts = this.charts$.getValue();
    const chart = charts.find(chartTemp => chartTemp.chartId === chartId);
    if(chart) {
      const sourceGroupColumnIndex = chart.chartDataSettings.sourceGroupColumns
        .findIndex(sourceGroupColumn => sourceGroupColumn.id === descriptorId);
      if(sourceGroupColumnIndex > -1) {
        chart.chartDataSettings.sourceGroupColumns.splice(sourceGroupColumnIndex, 1);
        this.updateOneChart(chart);
      }
    }
  }

  addSourceGroupNameColumn(sourceGroupNameColumn: Partial<ValueColumn>, chartId: string,index: number) {
    const chart = this.charts$.getValue().find(chartTemp => chartTemp.chartId === chartId);
    if(chart && index <= chart.chartDataSettings.sourceGroupNameColumns.length) {
      chart.chartDataSettings.sourceGroupNameColumns.splice(index, 0, sourceGroupNameColumn);
      if (this.selectedChart$.getValue()?.chartId === chart.chartId) {
        this.selectedChart$.next(chart);
      }
    }
  }

  deleteSourceGroupNameColumn(chartId: string, descriptorId: string){
    const charts = this.charts$.getValue();
    const chart = charts.find(chartTemp => chartTemp.chartId === chartId);
    if(chart) {
      const sourceGroupNameColumnIndex = chart.chartDataSettings.sourceGroupNameColumns
        .findIndex(sourceGroupNameColumn => sourceGroupNameColumn.id === descriptorId);
      if(sourceGroupNameColumnIndex > -1) {
        chart.chartDataSettings.sourceGroupNameColumns.splice(sourceGroupNameColumnIndex, 1);
        this.updateOneChart(chart);
      }
    }
  }

  addTargetGroupColumn(targetGroupColumn: Partial<ValueColumn>, chartId: string,index: number) {
    const chart = this.charts$.getValue().find(chartTemp => chartTemp.chartId === chartId);
    if(chart && index <= chart.chartDataSettings.targetGroupColumns.length) {
      chart.chartDataSettings.targetGroupColumns.splice(index, 0, targetGroupColumn);
      if (this.selectedChart$.getValue()?.chartId === chart.chartId) {
        this.selectedChart$.next(chart);
      }
    }
  }

  deleteTargetGroupColumn(chartId: string, descriptorId: string){
    const charts = this.charts$.getValue();
    const chart = charts.find(chartTemp => chartTemp.chartId === chartId);
    if(chart) {
      const targetGroupColumnIndex = chart.chartDataSettings.targetGroupColumns
        .findIndex(targetGroupColumn => targetGroupColumn.id === descriptorId);
      if(targetGroupColumnIndex > -1) {
        chart.chartDataSettings.targetGroupColumns.splice(targetGroupColumnIndex, 1);
        this.updateOneChart(chart);
      }
    }
  }

  addTargetGroupNameColumn(targetGroupNameColumn: Partial<ValueColumn>, chartId: string,index: number) {
    const chart = this.charts$.getValue().find(chartTemp => chartTemp.chartId === chartId);
    if(chart && index <= chart.chartDataSettings.targetGroupNameColumns.length) {
      chart.chartDataSettings.targetGroupNameColumns.splice(index, 0, targetGroupNameColumn);
      if (this.selectedChart$.getValue()?.chartId === chart.chartId) {
        this.selectedChart$.next(chart);
      }
    }
  }

  deleteTargetGroupNameColumn(chartId: string, descriptorId: string){
    const charts = this.charts$.getValue();
    const chart = charts.find(chartTemp => chartTemp.chartId === chartId);
    if(chart) {
      const targetGroupNameColumnIndex = chart.chartDataSettings.targetGroupNameColumns
        .findIndex(targetGroupNameColumn => targetGroupNameColumn.id === descriptorId);
      if(targetGroupNameColumnIndex > -1) {
        chart.chartDataSettings.targetGroupNameColumns.splice(targetGroupNameColumnIndex, 1);
        this.updateOneChart(chart);
      }
    }
  }

  addTooltipColumn(tooltipColumn: Partial<ValueColumn>, chartId: string, index: number): void {
    const chart = this.charts$.getValue().find(chartTemp => chartTemp.chartId === chartId);
    if(chart && index <= chart.chartDataSettings.tooltipColumns.length) {
      chart.chartDataSettings.tooltipColumns.splice(index, 0, tooltipColumn);
      chart.chartDataSettings.tooltipColumns.forEach((value, indexTemp) => value.priority = indexTemp + 1);
      if (this.selectedChart$.getValue()?.chartId === chart.chartId) {
        this.selectedChart$.next(chart);
      }
    }
  }

  updateTooltipColumn(chartId: string, id: string, newColumn: Partial<ValueColumn>): void {
    const charts = this.charts$.getValue();
    let chart = charts.find(chartTemp => chartTemp.chartId === chartId);
    if(chart) {
      const tooltipColumnIndex = chart.chartDataSettings.tooltipColumns
          .findIndex(tooltipColumn => tooltipColumn.id === id);
      if(tooltipColumnIndex > -1) {
        chart.chartDataSettings.tooltipColumns[tooltipColumnIndex] = newColumn;
        this.updateNameInChartFilters(chart, newColumn.columnId, newColumn.name);
        chart = {...chart};
        this.updateCharts(charts);
        this.selectedChart$.next(chart);
      }
    }
  }

  changeTooltipColumnPriority(tooltipColumn: Partial<ValueColumn>, newPriority: number,
                            currentIndex: number, previousIndex: number, chartId: string): void {
    const chart = this.charts$.getValue().find(chartTemp => chartTemp.chartId === chartId);
    if(chart) {
      chart.chartDataSettings.tooltipColumns = this.updateColumnsIndexesWithNewPriority(
          chart.chartDataSettings.tooltipColumns,tooltipColumn,previousIndex,currentIndex);
      this.updateOneChart(chart);
    }
  }

  deleteTooltipColumn(chartId: string, tooltipId: string): void {
    const charts = this.charts$.getValue();
    const chart = charts.find(chartTemp => chartTemp.chartId === chartId);
    if(chart) {
      const tooltipColumnIndex = chart.chartDataSettings.tooltipColumns
          .findIndex(valueColumn => valueColumn.id === tooltipId);
      if(tooltipColumnIndex > -1) {
        chart.chartDataSettings.tooltipColumns.splice(tooltipColumnIndex, 1);
        chart.chartDataSettings.tooltipColumns.forEach((value, index) => value.priority = index + 1);
      }
    }
  }

  deleteSortColumn(chartId: string, sortId: string): void {
    const charts = this.charts$.getValue();
    const chart = charts.find(chartTemp => chartTemp.chartId === chartId);
    if(chart) {
      const sortColumnIndex = chart.sortChartColumns
          .findIndex(valueColumn => valueColumn.id === sortId);
      if(sortColumnIndex > -1) {
        chart.sortChartColumns.splice(sortColumnIndex, 1);
        this.updateOneChart(chart);
      }
    }
  }

  changeDescriptorColumnPriority(descriptorColumn: Partial<DescriptorColumn>, newPriority: number,
                            currentIndex: number, previousIndex: number, chartId: string): void {
    const chart = this.charts$.getValue().find(chartTemp => chartTemp.chartId === chartId);
    if(chart) {
      chart.chartDataSettings.descriptorColumns = this.updateColumnsIndexesWithNewPriority(
          chart.chartDataSettings.descriptorColumns,descriptorColumn,previousIndex,currentIndex);
      this.updateOneChart(chart);
    }
  }
  changeValueColumnPriority(valueColumn: Partial<ValueColumn>, newPriority: number,
                            currentIndex: number, previousIndex: number, chartId: string): void {
    const chart = this.charts$.getValue().find(chartTemp => chartTemp.chartId === chartId);
    if(chart) {
      chart.chartDataSettings.valueColumns = this.updateColumnsIndexesWithNewPriority(
          chart.chartDataSettings.valueColumns,valueColumn,previousIndex,currentIndex);
      this.updateOneChart(chart);
    }
  }

  changeSortColumnPriority(sortColumn: Partial<ChartInteractiveSort>, newPriority: number,
    currentIndex: number, previousIndex: number, chartId: string): void {
    const chart = this.charts$.getValue().find(chartTemp => chartTemp.chartId === chartId);
    if(chart) {
      chart.sortChartColumns = this.updateColumnsIndexesWithNewPriority(
          chart.sortChartColumns,sortColumn,previousIndex,currentIndex);
      this.updateOneChart(chart);
    }
  }

  changeLegendValueColumnPriority(valueColumn: Partial<ValueColumn>, newPriority: number,
                            currentIndex: number, previousIndex: number, chartId: string): void {
    const chart = this.charts$.getValue().find(chartTemp => chartTemp.chartId === chartId);
    if(chart) {
      chart.chartDataSettings.legendColumns = this.updateColumnsIndexesWithNewPriority(
        chart.chartDataSettings.legendColumns,valueColumn,previousIndex,currentIndex);
      this.updateOneChart(chart);
    }
  }

  changeSecondValueColumnPriority(valueColumn: Partial<ValueColumn>, newPriority: number,
                                  currentIndex: number, previousIndex: number, chartId: string): void {
    const chart = this.charts$.getValue().find(chartTemp => chartTemp.chartId === chartId);
    if(chart) {
      chart.chartDataSettings.secondValueColumns = this.updateColumnsIndexesWithNewPriority(
          chart.chartDataSettings.secondValueColumns,valueColumn,previousIndex,currentIndex);
      this.updateOneChart(chart);
    }
  }

  updateColumnsIndexesWithNewPriority(values: Partial<ValueColumn | DescriptorColumn>[],
                                      valueColumn: Partial<ValueColumn | DescriptorColumn>, previousIndex: number,
                                      currentIndex: number): Partial<ValueColumn | DescriptorColumn>[] {
    if(!Array.isArray(values)) {
      return [];
    }
    if(previousIndex < 0 || previousIndex > values.length - 1 || currentIndex < 0 || currentIndex > values.length - 1) {
      return values;
    }
    values.splice(previousIndex, 1);
    values.splice(currentIndex, 0, valueColumn);
    values.forEach((value,index) => value.priority = index + 1);
    return [...values];
  }

  findMissedColumns(column: Partial<ValueColumn | DescriptorColumn | LegendColumn | SecondValueColumn>):
    Partial<ValueColumn | DescriptorColumn | LegendColumn | SecondValueColumn> {
    const dataSource = this.dataSourcesOfDashboard$.getValue()
      .find(datasource => datasource.dataSourceId === column.dataSourceId);
    const parameter = this.parametersOfDashboard$.getValue()
        .find(datasource => datasource.dataSourceId === column.dataSourceId);
    if(!dataSource && !parameter) {
      return column;
    }

    const tempColumn = dataSource ?
        dataSource.dashboardColumns.find(item => item.columnId === column.columnId) :
        parameter.dashboardColumns.find(item => item.columnId === column.columnId);
    if(!tempColumn) {
      return column;
    }

    column.columnDataModuleType = tempColumn.dataModuleType;
    column.columnDataType = tempColumn.dataType;
    column.columnType = tempColumn.columnType;
    column.dataModuleType = tempColumn.dataModuleType;
    column.name = tempColumn.name;
    return column;
  }

  findMissedSort(column: Partial<ChartInteractiveSort>): Partial<ChartInteractiveSort> {
    const dataSource = this.dataSourcesOfDashboard$.getValue()
        .find(datasource => datasource.dataSourceId === column.dataSourceId);
    const parameter = this.parametersOfDashboard$.getValue()
        .find(datasource => datasource.dataSourceId === column.dataSourceId);
    if(!dataSource && !parameter) {
      return column;
    }

    const tempColumn = dataSource ?
        dataSource.dashboardColumns.find(item => item.columnId === column.columnId) :
        parameter.dashboardColumns.find(item => item.columnId === column.columnId);
    if(!tempColumn) {
      return column;
    }

    column.columnDataModuleType = tempColumn.dataModuleType;
    column.columnDataType = tempColumn.dataType;
    column.columnType = tempColumn.columnType;
    column.name = tempColumn.name;
    return column;
  }

  findMissedFilters(filter: Partial<Filter>): Partial<Filter> {
    const dataSource = this.dataSourcesOfDashboard$.getValue()
      .find(datasource => datasource.dataSourceId === filter.dataSourceId);
    const parameter = this.parametersOfDashboard$.getValue()
        .find(datasource => datasource.dataSourceId === filter.dataSourceId);
    if(!dataSource && !parameter) {
      return filter;
    }

    const tempColumn = dataSource ?
        dataSource.dashboardColumns.find(item => item.columnId === filter.columnId) :
        parameter.dashboardColumns.find(item => item.columnId === filter.columnId);
    if(!tempColumn) {
      return filter;
    }

    filter.columnDataModuleType = tempColumn.dataModuleType;
    filter.columnDataType = tempColumn.dataType;
    filter.columnType = tempColumn.columnType;
    filter.columnName = tempColumn.name;
    return filter;
  }


//chart
  getSelectedChart$(): Observable<Partial<ChartModel>>{
    return this.selectedChart$.asObservable();
  }
  getSelectedChartValue(): Partial<Partial<ChartModel>> {
    return this.selectedChart$.getValue();
  }
  getSelectedChartFilters$(): Observable<Partial<Filter>[]> {
    return this.selectedChartFilters$.asObservable();
  }
  getSelectedChartFiltersValue() {
    return this.selectedChartFilters$.value;
  }
  updateSelectedChartFilters(filters: Filter[]): void {
    this.selectedChartFilters$.next(this.setFilterColumnsIsMissed(filters));
  }

  refreshSelectedChartFiltersMissingState(): void {
    const currentFilters = this.selectedChartFilters$.getValue();
    if (currentFilters) {
      this.selectedChartFilters$.next(this.setFilterColumnsIsMissed(currentFilters));
    }
  }
  updateSingleSelectedChartFilter(filter: Partial<Filter>): void {
    const currentFilters = this.selectedChartFilters$.getValue();
    const filterIndex = currentFilters.findIndex((c) => c.filterId === filter.filterId);
    if(filterIndex > -1) {
      currentFilters[filterIndex] = filter;
      this.selectedChartFilters$.next([...currentFilters]);
    }
  }
  createChartFilter(filter: Filter, filterId: string): void {
    filter.columnName = this.getColumnName(filter.columnId, filter.dataSourceId);
    filter.columnDataModuleType = this.getColumnDataModuleType(filter.columnId, filter.dataSourceId);
    filter.columnDataType = this.getColumnDataType(filter.columnId, filter.dataSourceId);
    filter.columnIsMissed = this.columnIsMissed(filter.dataSourceId, filter.columnId);
    filter.filterId = filterId;
    this.selectedChartFilters$.next([...this.selectedChartFilters$.getValue(),filter]);
  }
  deleteChartFilter(filterId): void {
    const currentFilters = this.selectedChartFilters$.getValue();
    this.selectedChartFilters$.next(currentFilters.filter(f => f.filterId !== filterId));
  }
  setSelectedChart(chart: Partial<ChartModel>): void {
    this.previousSelectedControllerValue = {...this.selectedControllerValue};
    this.selectedControllerValue = chart && chart.isController ? this.createDefaultController(chart) : undefined;
    if (chart !== undefined) {
      this.selectedChart$.next(this.setChartColumnsIsMissed(chart));
      this.updateRollbackChartDesignSetting(chart);
      this.updateRollbackChartAnnotationSetting(chart);
    } else {
      this.selectedChart$.next(chart);
    }
  }

  setChartColumnsIsMissed(chart: Partial<ChartModel>): Partial<ChartModel> {
      chart.chartDataSettings?.descriptorColumns?.forEach(
        (column) => {
          if (column.columnIsMissed || column.columnIsMissed === undefined) {
            column.columnIsMissed = this.columnIsMissed(column.dataSourceId, column.columnId);
            if (!column.columnIsMissed && (column.dataModuleType === 'None' || column.columnDataModuleType === 'None')) {
              column = this.findMissedColumns(column);
            }
          }
        },
      );
      chart.chartDataSettings?.legendColumns?.forEach(
        (column) => {
          if (column.columnIsMissed || column.columnIsMissed === undefined) {
            column.columnIsMissed = this.columnIsMissed(column.dataSourceId, column.columnId);
            if (!column.columnIsMissed && (column.dataModuleType === 'None' || column.columnDataModuleType === 'None')) {
              column = this.findMissedColumns(column);
            } else if (column.columnIsMissed && !chart.hasError &&
              (column.dataModuleType === 'None' || column.columnDataModuleType === 'None')) {
              chart.hasError = true;
            };
          }
        },
      );
      chart.chartDataSettings?.valueColumns?.forEach(
        (column) => {
          if (column.columnIsMissed || column.columnIsMissed === undefined) {
            column.columnIsMissed = this.columnIsMissed(column.dataSourceId, column.columnId);
            if (!column.columnIsMissed && (column.dataModuleType === 'None' || column.columnDataModuleType === 'None')) {
              column = this.findMissedColumns(column);
            }
          }
        },
      );
      chart.chartDataSettings?.secondValueColumns?.forEach(
        (column) => {
          if (column.columnIsMissed || column.columnIsMissed === undefined) {
            column.columnIsMissed = this.columnIsMissed(column.dataSourceId, column.columnId);
            if (!column.columnIsMissed && (column.dataModuleType === 'None' || column.columnDataModuleType === 'None')) {
              column = this.findMissedColumns(column);
            }
          }
        },
      );
      chart.chartDataSettings?.tooltipColumns?.forEach(
          (column) => {
            if (column.columnIsMissed || column.columnIsMissed === undefined) {
              column.columnIsMissed = this.columnIsMissed(column.dataSourceId, column.columnId);
              if (!column.columnIsMissed && (column.dataModuleType === 'None' || column.columnDataModuleType === 'None')) {
                column = this.findMissedColumns(column);
              }
            }
          },
      );
      if (Array.isArray(chart.sortChartColumns)) {
        chart.sortChartColumns?.forEach(
          (column) => {
            if (column.columnIsMissed || column.columnIsMissed === undefined) {
              column.columnIsMissed = this.columnIsMissed(column.dataSourceId, column.columnId);
              if (!column.columnIsMissed && (column.dataModuleType === 'None' || column.columnDataModuleType === 'None')) {
                column = this.findMissedColumns(column);
              }
            }
          },
        );
      }
    return chart;
  }
  checkChartShouldReload(chart: Partial<ChartModel>, datasourceId: string): boolean {
    let columnsLength = chart.chartDataSettings?.descriptorColumns.length;
    if (columnsLength > 0) {
      for (let i = 0; i < columnsLength; i++) {
        if (chart.chartDataSettings?.descriptorColumns[i].dataSourceId === datasourceId) {
          return true;
        }
      }
    }
    columnsLength = chart.chartDataSettings?.legendColumns.length;
    if (columnsLength > 0) {
      for (let i = 0; i < columnsLength; i++) {
        if (chart.chartDataSettings?.legendColumns[i].dataSourceId === datasourceId) {
          return true;
        }
      }
    }
    columnsLength = chart.chartDataSettings?.secondValueColumns.length;
    if (columnsLength > 0) {
      for (let i = 0; i < columnsLength; i++) {
        if (chart.chartDataSettings?.secondValueColumns[i].dataSourceId === datasourceId) {
          return true;
        }
      }
    }
    columnsLength = chart.chartDataSettings?.valueColumns.length;
    if (columnsLength > 0) {
      for (let i = 0; i < columnsLength; i++) {
        if (chart.chartDataSettings?.valueColumns[i].dataSourceId === datasourceId) {
          return true;
        }
      }
    }
    return false;
  }

  getCharts$(): Observable<Partial<ChartModel>[]> {
    return this.charts$.asObservable();
  }
  getChartValue(chartId: string): Partial<ChartModel> {
    return this.charts$.getValue().find(chart => chart.chartId === chartId);
  }
  updateCharts(charts: Partial<ChartModel>[]): void {
    charts.map(chart => this.setChartColumnsIsMissed(chart));
    const updatedDashboard = this.dashboard$.getValue();
    if(updatedDashboard){
      updatedDashboard.charts = [...charts];
      this.dashboard$.next({...updatedDashboard});
    }
    this.charts$.next(charts);
  }

  updateRollbackChartDesignSetting(newChart: Partial<ChartModel>): void {
    this.rollbackChartDesignSetting$.next(newChart);
  }

  getRollbackChartDesignSettingValue(): Partial<ChartModel> {
    return this.rollbackChartDesignSetting$.getValue();
  }

  rollBackChartDesignSetting(): void {
    this.updateOneChart(this.getRollbackChartDesignSettingValue());
  }

  updateRollbackChartAnnotationSetting(newChart: Partial<ChartModel>): void {
    this.rollbackChartAnnotationSetting$.next(newChart);
  }

  getRollbackChartAnnotationSettingValue(): Partial<ChartModel> {
    return this.rollbackChartAnnotationSetting$.getValue();
  }

  rollBackChartAnnotationSetting(): void {
    this.updateOneChart(this.getRollbackChartAnnotationSettingValue());
  }


  updateOneChart(chart: Partial<ChartModel>){
    if(chart?.chartId) {
      const charts = this.charts$.getValue();
      const index = charts.findIndex((c) => c.chartId === chart.chartId);
      charts[index] = {...chart};
      this.updateCharts(charts);
      if(this.selectedChart$.getValue()?.chartId === chart.chartId) {
        this.selectedChart$.next(charts[index]);
      }
    }
  }

  updateChartData(columnsData: ColumnData[], chartId: string, mapTopo?: any, result?: any, totalRowCount?:number){
    const charts = this.charts$.getValue();
    const index = charts.findIndex((c) => c.chartId === chartId);
    if(index > -1 ) {
      const dropDownControllerHasDefaultValues =charts[index].isController &&
          charts[index].chartType === 'DropdownController' &&
          columnsData.length > 0 &&
          charts[index].defaultValuesForController.defaultValues.length > 0;
      if(dropDownControllerHasDefaultValues) {
        columnsData[0].data = [...charts[index].defaultValuesForController.defaultValues,...columnsData[0].data ];
      }
      charts[index].columnsData = columnsData;

      // set noDataExist based on columnsData
      if (columnsData && columnsData.length > 0) {
        const hasAnyData = columnsData.some(column =>
          column.data && column.data.length > 0
        );
        charts[index].noDataExist = !hasAnyData;
      }
      // else {
      //   charts[index].noDataExist = true;
      // }

      charts[index].hasError = false;
      if(charts[index].chartType === 'TableChart'){
        charts[index].totalRowCount = totalRowCount;
      }
      charts[index].needToReload = false;
      charts[index].mapTopo = mapTopo;
      if(result !== null && result !== undefined){
        charts[index].result = JSON.parse(result);
      }
      charts[index].isLoading= false;
      charts[index] = {...charts[index]};
      this.updateCharts(charts);
      if(this.selectedChart$.getValue()?.chartId === chartId) {
        this.selectedChart$.next(charts[index]);
      }
    }
  }

  setMissingChartsColumns(charts: Partial<ChartModel>[], dataSourceId: string,
                          columnId: string = undefined): Partial<ChartModel>[] {
    charts.forEach(chart => {
      chart.chartDataSettings.descriptorColumns.forEach(column => {
        const columnIsMissed = (columnId == null ? true : column.columnId === columnId) &&
            column.dataSourceId === dataSourceId;
        if (columnIsMissed) {
          column.dataModuleType = 'None';
          column.columnDataModuleType = 'None';
          column.name = '';
          column.columnIsMissed = true;
          chart.hasError = true;
        }
      });
      chart.chartDataSettings.legendColumns.forEach(column => {
        const columnIsMissed = (columnId == null ? true : column.columnId === columnId) &&
            column.dataSourceId === dataSourceId;
        if (columnIsMissed) {
          column.dataModuleType = 'None';
          column.columnDataModuleType = 'None';
          column.name = '';
          column.columnIsMissed = true;
          chart.hasError = true;
        }
      });
      chart.chartDataSettings.secondValueColumns.forEach(column => {
        const columnIsMissed = (columnId == null ? true : column.columnId === columnId) &&
            column.dataSourceId === dataSourceId;
        if (columnIsMissed) {
          column.dataModuleType = 'None';
          column.columnDataModuleType = 'None';
          column.name = '';
          column.columnIsMissed = true;
          chart.hasError = true;
        }
      });
      chart.chartDataSettings.valueColumns.forEach(column => {
        const columnIsMissed = (columnId == null ? true : column.columnId === columnId) &&
            column.dataSourceId === dataSourceId;
        if (columnIsMissed) {
          column.dataModuleType = 'None';
          column.columnDataModuleType = 'None';
          column.name = '';
          column.columnIsMissed = true;
          chart.hasError = true;
        }
      });
      chart.chartDataSettings.tooltipColumns.forEach(column => {
        if (column.dataSourceId === dataSourceId && column.columnId === columnId) {
          column.dataModuleType = 'None';
          column.columnDataModuleType = 'None';
          column.name = '';
          column.columnIsMissed = true;
          chart.hasError = true;
        }
      });
      if (Array.isArray(chart.sortChartColumns)) {
        chart.sortChartColumns.forEach(column => {
          if (column.dataSourceId === dataSourceId && column.columnId === columnId) {
            column.dataModuleType = 'None';
            column.columnDataModuleType = 'None';
            column.name = '';
            column.columnIsMissed = true;
            chart.hasError = true;
          }
        });
      }
    });
    return charts;
  }

  setSelectedChartMissingFilter(dataSourceId: string, columnId: string = undefined): void {
    const currentFilters = this.selectedChartFilters$.getValue();
    if (currentFilters) {
      currentFilters.forEach(filter => {
        const columnIsMissed = (columnId == null ? true : filter.columnId === columnId) &&
            filter.dataSourceId === dataSourceId;
        if (columnIsMissed) {
          filter.columnDataModuleType = 'None';
          filter.columnName = '';
          filter.columnIsMissed = true;
          filter.columnType = 'None';
        }
      });
      this.selectedChartFilters$.next([...currentFilters]);
    }
  }

  appendChartData(columnsData: ColumnData[], chartId: string, totalRowCount?:number){
    const charts = this.charts$.getValue();
    const index = charts.findIndex((c) => c.chartId === chartId);
    if( index > -1 ) {
      charts[index].columnsData.forEach((columnData, i) => {
        const data = columnsData.find(column => column.columnId === columnData.columnId).data.splice(1);
        charts[index].columnsData[i].data = charts[index].columnsData[i].data.concat(data);
      });

      // set boDataExist based on columnsData after data append
      if (charts[index].columnsData && charts[index].columnsData.length > 0) {
        const hasAnyData = charts[index].columnsData.some(column =>
          column.data && column.data.length > 0
        );
        charts[index].noDataExist = !hasAnyData;
      }
      // else {
      //   charts[index].noDataExist = true;
      // }

      if(charts[index].chartType === 'TableChart'){
        charts[index].totalRowCount = totalRowCount;
      }
      charts[index].hasError = false;
      charts[index].needToReload = false;
      charts[index].isLoading = false;
      charts[index] = {...charts[index]};
      this.updateCharts(charts);
      if(this.selectedChart$.getValue()?.chartId === chartId) {
        this.selectedChart$.next(charts[index]);
      }
    }
  }

  setChartError(chartId: string, errorMessage: string): void {
    const charts = this.charts$.getValue();
    const index = charts.findIndex((c) => c.chartId === chartId);
    if(index > -1) {
      charts[index].hasError = true;
      charts[index].needToReload = false;
      charts[index].isLoading = false;
      charts[index].errorMessage = errorMessage;
      charts[index].noDataExist = false;
      this.updateOneChart(charts[index]);
    }
  }

  setChartNeedToReload(chartId: string): void {
    const charts = this.charts$.getValue();
    const index = charts.findIndex((c) => c.chartId === chartId);
    if (index > -1) {
      charts[index].needToReload = true;
      charts[index].hasError = false;
      charts[index].isLoading = false;
      this.updateOneChart(charts[index]);
    }
  }

  createChart(chart: Partial<ChartModel>): void {
    chart.controllersValues = this.controllers$.getValue()?.map(temp => temp.controllerValue) ?? [];
    chart.chartIndexInDashboard = this.charts$.getValue().length;
    const charts = this.charts$.getValue();
    const newChartsDrillDownMode = this.chartsDrillDownMode$.getValue();
    newChartsDrillDownMode.push(false);
    this.chartsDrillDownMode$.next(newChartsDrillDownMode);
    this.updateCharts([...charts, chart]);
    this.newChart$.next(chart);
    this.setSettingSidebarId(chart.chartType);
    this.dashboardSettingSidebarToggleCompact(true);
    this.addNewChartUserFilters();
    this.setSelectedChart(chart);
    this.updateSelectedChartFilters([]);
  }

  getNewChart$(): Observable<any>{
    return this.newChart$.asObservable();
  }

  getNewChartValue(): any {
    return this.newChart$.getValue();
  }

  setExportChartDataDetail(data: IExportChartData): void {
    this.exportChartDataDetail$.next(data);
  }

  getExportChartDataDetail$(): Observable<IExportChartData> {
    return this.exportChartDataDetail$.asObservable();
  }

  //settings sidebar
  dashboardSettingIsCompacted$(): Observable<boolean>{
    return this.dashboardSettingSidebarCompacted$.asObservable();
  }

  dashboardSettingSidebarToggleCompact(compacted: boolean): void {
    if(this.dashboardSettingSidebarCompacted$.getValue() !== compacted) {
      this.dashboardSettingSidebarCompacted$.next(compacted);
    }
  }

  setDashboardSettingChangeSizeEvent(event: void): void {
    this.dashboardSettingChangeSizeEvent$.next(event);
  }

  getDashboardSettingChangeSizeEvent$(): Observable<void> {
    return this.dashboardSettingChangeSizeEvent$.asObservable();
  }

  getSettingSidebarId$(): Observable<ChartType>{
    return this.settingSidebarId$.asObservable();
  }

  setSettingSidebarId(settingSidebarId: ChartType): void {
    this.settingSidebarId$.next(settingSidebarId);
  }

  dashboardIsFullSize(): Observable<boolean> {
    return this.dashboardFullSize$.asObservable();
  }

  toggleDashboardFullSize(fullSize?: boolean) {
    this.dashboardFullSize$.next((fullSize === undefined) ? (!this.dashboardFullSize$.getValue()) : fullSize);
    if(this.dashboardFullSize$.getValue()){
      this.setSettingSidebarId('dashboard');
      this.setSelectedChart(undefined);
    }
  }

  chartIsFullSize(): Observable<boolean> {
    return this.chartFullSize$.asObservable();
  }

  toggleChartFullSize(fullSize?: boolean) {
    this.chartFullSize$.next(fullSize);
    if (fullSize){
      this.setSettingSidebarId('dashboard');
      this.setSelectedChart(undefined);
    }
    this.resetChartsZoomStatus(this.charts$.getValue());
  }

  deleteChart(chartId: string){
    const chart = this.charts$.getValue().find((chartTemp) => chartTemp.chartId === chartId);
    const newChartsDrillDownValues = this.chartsDrillDownValues$.getValue();
    const newChartsDrillDownMode = this.chartsDrillDownMode$.getValue();
    let newCharts = this.charts$.getValue();
    if(chart) {
      newCharts.splice(chart.chartIndexInDashboard,1);
      newCharts = this.indexingChartsInDashboard(newCharts);
      this.updateCharts(newCharts);
      newChartsDrillDownValues.splice(chart.chartIndexInDashboard,1);
      this.chartsDrillDownValues$.next([...newChartsDrillDownValues]);
      newChartsDrillDownMode.splice(chart.chartIndexInDashboard, 1);
      this.chartsDrillDownMode$.next(newChartsDrillDownMode);
      this.removeChartUserFilters(chart.chartIndexInDashboard);
      const selectedChart = this.selectedChart$.getValue();
      if(chart.chartId === selectedChart?.chartId) {
        this.selectedChart$.next(null);
      }
    }
  }

  deleteGridItem(gridId: string){
    const dashboard = this.dashboard$.getValue();
    const gridItemIndex = dashboard.chartsGrid.grid.findIndex((item) => item.id === gridId);
    if(gridItemIndex > -1) {
      dashboard.chartsGrid.grid.splice(gridItemIndex,1);
      this.updateDashboard(dashboard);
    }
    return dashboard;
  }

  copyChart(chart: Partial<ChartModel>){
    const newCharts = this.charts$.getValue();
    if(chart) {
      this.setSelectedChart(chart);
      newCharts.push(chart);
      this.updateCharts(newCharts);
      this.hasChangedDesignSetting = true;
    }
  }

  copyGridItem(gridItem: GridItem){
    const dashboard = this.dashboard$.getValue();
    dashboard.chartsGrid.grid.push(gridItem);
    this.updateDashboard(dashboard);
    return dashboard;
  }

  //filters
  getDashboardFilters$(): Observable<Partial<Filter>[]> {
    return this.dashboardFilters$.asObservable();
  }

  createDashboardFilter(filter: Filter, filterId: string): void {
    const currentFilters = this.dashboardFilters$.getValue();
    filter.filterId = filterId;
    filter.columnDataModuleType = this.getColumnDataModuleType(filter.columnId, filter.dataSourceId);
    filter.columnDataType = this.getColumnDataType(filter.columnId, filter.dataSourceId);
    filter.columnName = this.getColumnName(filter.columnId, filter.dataSourceId);
    filter.columnIsMissed = this.columnIsMissed(filter.dataSourceId, filter.columnId);
    currentFilters.push(filter);
    this.dashboardFilters$.next([...currentFilters]);
  }

  updateDashboardFilters(filters: Filter[]): void {
    this.dashboardFilters$.next(this.setFilterColumnsIsMissed(filters));
  }

  setFilterColumnsIsMissed(filters: Partial<Filter>[]): Partial<Filter>[] {
    if (filters?.length) {
      filters.forEach(filter => {
        if (filter.columnIsMissed || filter.columnIsMissed === undefined) {
          filter.columnIsMissed = this.columnIsMissed(filter.dataSourceId, filter.columnId);
          if (!filter.columnIsMissed && filter.columnDataModuleType === 'None') {
            filter = this.findMissedFilters(filter);
          }
        }
      });
    }
    return filters;
  }

  updateSingleDashboardFilter(filter: Partial<Filter>): void {
    const currentFilters = this.dashboardFilters$.getValue();
    const foundedFilterIndex = currentFilters.findIndex((c) => c.filterId === filter.filterId);
    if(foundedFilterIndex > -1) {
      currentFilters[foundedFilterIndex] = filter;
      this.dashboardFilters$.next([...currentFilters]);
    }
  }

  deleteDashboardFilter(filter: Partial<Filter>): void {
    const currentFilters = this.dashboardFilters$.value;
    const index = currentFilters.findIndex((c) => c.filterId === filter.filterId);
    if(index > -1) {
      currentFilters.splice(index,1);
      this.dashboardFilters$.next(currentFilters);
    }
  }

  getFilterSubmitting$(): Observable<Submit> {
    return this.filterSubmitting$.asObservable();
  }

  updateFilterSubmitting(submitting: Submit): void {
    this.filterSubmitting$.next(submitting);
  }

  // comments
  updateComments(comments: Comment[]): void {
    const updateComments = this.setReplyComments(this.setWordsOfComments(comments));
    this.comments$.next(updateComments);
  }

  addComment(newComment: Comment): void {
    const currComments = this.comments$.getValue();
    const commentState = this.commentState$.getValue();
    const commentType = this.commentsType$.getValue();
    newComment.replies = [];
    if(commentState === 'Post' && (commentType === 'All' || commentType ==='NotResolved')) {
      currComments.push(this.setWordsOfComments([newComment])[0]);
    }
    if(commentState === 'Reply' && (commentType === 'All' || commentType ==='NotResolved')) {
      const foundedIndex = currComments.findIndex(comment => comment.id === newComment.parentCommentId);
      if(foundedIndex > -1) {
        currComments[foundedIndex].replies.push(this.setWordsOfComments([newComment])[0]);
        currComments[foundedIndex] = {...currComments[foundedIndex]};
      }
    }
    this.comments$.next([...currComments]);
  }

  updateComment(newComment: Comment,isResolved: boolean): void {
    const currentComments = this.comments$.getValue();
    const commentsType = this.commentsType$.getValue();
    const commentState = this.commentState$.getValue();
    if(commentState !== 'EditReply') {
      const foundedIndex = currentComments.findIndex(comment => comment.id === newComment.id);
      if(foundedIndex > -1) {
        if((commentsType === 'NotResolved' && isResolved) || (commentsType === 'Resolved' && !isResolved)) {
          currentComments.splice(foundedIndex,1);
        } else if(commentsType === 'Mentioned' && newComment.mentionedUserIds.length === 0 && !newComment.isReply) {
          currentComments.splice(foundedIndex,1);
        } else {
          currentComments[foundedIndex] = this.editComment(currentComments[foundedIndex], newComment.comment, isResolved);
        }
        this.comments$.next(currentComments);
      }
    } else {
      const foundedParentIndex = currentComments.findIndex(comment => comment.id === newComment.parentCommentId);
      if(foundedParentIndex > -1) {
        const foundedReplyIndex = currentComments[foundedParentIndex].replies.
        findIndex(comment => comment.id === newComment.id);
        if(foundedReplyIndex > -1) {
          currentComments[foundedParentIndex].replies[foundedReplyIndex] = this.editComment
          (currentComments[foundedParentIndex].replies[foundedReplyIndex], newComment.comment, isResolved);
          currentComments[foundedParentIndex] = {...currentComments[foundedParentIndex]};
          this.comments$.next(currentComments);
        }
      }
    }
  }

  editComment(currentComment: Comment,newCommentText: string,isResolved: boolean): Comment {
    currentComment.comment = newCommentText;
    currentComment.isResolved = isResolved;
    currentComment.lastModifiedTickTime = new Date().getTime() * 10000 + 621355968000000000;
    currentComment = this.setWordsOfComments([currentComment])[0];
    return {...currentComment};
  }

  getComments$(): Observable<Comment[]> {
    return this.comments$.asObservable();
  }

  updateToggleDashboardComments(commentsDisplayed: boolean): void {
    if(this.toggleDashboardComments$.getValue() !== commentsDisplayed) {
      this.toggleDashboardComments$.next(commentsDisplayed);
    }
  }

  dashboardCommentsDisplayed$(): Observable<boolean> {
    return this.toggleDashboardComments$.asObservable();
  }

  setUsersToMention(users: UserMention[]): void {
    this.usersToMention = users;
  }

  setCommentState(state: CommentState): void {
    this.commentState$.next(state);
  }

  getCommentState$(): Observable<CommentState> {
    return this.commentState$.asObservable();
  }


  setCommentsType(commentType: CommentType): void {
    this.commentsType$.next(commentType);
  }

  setWordsOfComments(comments: Comment[]): Comment[] {
    const usersToMention = this.usersToMention;
    if(!Array.isArray(comments)) {
      return [];
    }
    comments.forEach(comment =>  {
      comment['words'] = comment.comment.split(' ');
      comment['mentionedWordIndexes'] = [];
      comment.words.forEach((word,wordIndex) => {
        if(word.includes('@')) {
          for(let userIndex = 0; userIndex < usersToMention.length; userIndex++) {
            if(usersToMention[userIndex].username.toLowerCase() === word.toLowerCase().substr(1)) {
              comment.mentionedWordIndexes.push(wordIndex);
              break;
            }
          }
        }
      });
    });
    return [...comments];
  }

  setReplyComments(comments: Comment[]): Comment[] {
    const commentsType = this.commentsType$.getValue();
    comments.forEach(comment => comment.replies = []);
    if(commentsType === 'Mentioned') {
      return [...comments];
    }
    const parentComments = comments.filter(comment => !comment.isReply);
    const replyComments = comments.filter(comment => comment.isReply);
    replyComments.forEach((replyComment) => {
      for(let parentIndex = 0; parentIndex < parentComments.length; parentIndex++) {
        if(replyComment.parentCommentId === parentComments[parentIndex].id) {
          parentComments[parentIndex].replies.push(replyComment);
          break;
        }
      }
    });
    return [...parentComments];
  }

  findMentionedIds(newComment: any): any {
    const users = this.usersToMention;
    newComment.words  = newComment.comment.split(' ');
    newComment.mentionedUserIds = [];
    newComment.words.forEach(word => {
      if(word.includes('@')) {
        for (let index = 0 ; index < users.length; index++) {
          if(users[index].username.toLowerCase() === word.substring(1).toLowerCase()) {
            newComment.mentionedUserIds.push(users[index].id);
            break;
          }
        }
      }
    });

    return {...newComment};
  }

  updateFilterColumnValues(values ,filterId: string, appendData: boolean, searchKey: string = ''): void {
    const selectedChartFilters = this.selectedChartFilters$.getValue();
    const dashboardFilters = this.dashboardFilters$.getValue();
    let filter;
    if(selectedChartFilters !== undefined) {
      filter = selectedChartFilters.find(filterTemp => filterTemp.filterId === filterId);
    }
    if(filter === undefined && dashboardFilters !== undefined) {
      filter = dashboardFilters.find(filterTemp => filterTemp.filterId === filterId);
    }
    const currentColumnValues = this.selectedFilterColumnValues$.getValue() ?? [];
    let newColumnValues = [];
    let selectedColumnValues = [];
    if(filter) {
        newColumnValues = this.mapFilterColumnValues(values);
        if(searchKey && searchKey.trim() !== '') {
          this.selectedFilterColumnValues$.next(newColumnValues);
        } else {
         if(appendData) {
            const mergedValues = [...currentColumnValues].concat([...newColumnValues]);
            this.selectedFilterColumnValues$.next(mergedValues);
           } else {
            selectedColumnValues = this.mapFilterColumnValues(filter.selectedValues,true);

            const mergedValues = [...selectedColumnValues].concat([...newColumnValues]);
            this.selectedFilterColumnValues$.next(mergedValues);
            }
        }
    }
  }

  getSelectedFilterColumnValues$(): Observable<any> {
    return this.selectedFilterColumnValues$.asObservable().pipe(skip(1));
  }

  mapFilterColumnValues(values, checked: boolean = false): any {
    if(!Array.isArray(values)) {
      return [];
    }
    return values.map((value, index) => ({
      value,
      checked,
    }));
  }

  resetStates(): void {
    this.toggleDashboardComments$.next(false);
    this.comments$.next(null);
    this.dashboardType$.next('None');
    this.dashboard$.next(null);
    this.dashboardUpdating$.next(false);
    this.dataSourcesOfDashboard$.next([]);
    this.selectedChart$.next(null);
    this.charts$.next([]);
    this.newChart$.next(null);
    this.dashboardSettingSidebarCompacted$.next(true);
    this.settingSidebarId$.next('dashboard');
    this.dashboardFullSize$.next(false);
    this.chartFullSize$.next(undefined);
    this.dashboardFilters$.next([]);
    this.filterSubmitting$.next(Submit.notSubmitted);
    this.controllers$.next([]);
    this.controllersFilters$.next([]);
    this.interactionCharts$.next([]);
    this.editInteractionMode$.next(false);
    this.chartsHasUserSort$.next([]);
    this.shouldSaveDashboard$.next(false);
    this.hasChangedDesignSetting = false;
    this.hasChangedChartGrids = false;
    this.chartInsight$.next(null);
    this.dashboardRecoverySuccess.set(false);
    this.dashboardRecoveryLoading.set(false);
    this.dashboardNameDuplicated.set(false)
  }

  resetInsights(): void {
    this.chartInsight$.next(null);
  }

  resetComments(): void {
    this.comments$.next(null);
  }

  // coloring methods
  updateChartColors(chart: Partial<ChartModel>, columnColor: Partial<ColumnColor>): void {
    const columnDataIndex = chart.columnsData.findIndex(columnData => columnData.id === columnColor.columnInChartId);
    if(columnDataIndex > -1) {
      this.previousSelectedChartColors = {
        colors: chart.columnsData[columnDataIndex].colors,
        columnDataIndex,
      };
      chart.columnsData[columnDataIndex].colors = {...columnColor.colors};
      if(chart.chartType === 'SankeyChart' || chart.chartType === 'CollapsableNetworkChart') {
        chart.columnsData = this.updateSankeyChartColors(chart.columnsData[columnDataIndex], chart.columnsData);
      }
      const charts = this.charts$.getValue();
      const foundedIndex = charts.findIndex(tempChart => tempChart.chartId === chart.chartId);
      if(foundedIndex > -1) {
        charts[foundedIndex] = {...chart};
        this.updateCharts(charts);
      }
      this.selectedChart$.next({...chart});
    }
  }

  updateSankeyChartColors(columnData: ColumnData, columnsData: ColumnData[]): ColumnData[] {
    if(columnData.chartColumnType === 'Descriptor') {
      const updateColumnData = columnsData.find(column => column.chartColumnType === 'Legend');
      const colorKeys = Object.keys(columnData.colors);
      colorKeys.forEach(colorKey => {
        if(updateColumnData.colors[colorKey] !== undefined) {
          updateColumnData.colors[colorKey] = columnData.colors[colorKey];
        }
      });
      return [...columnsData];
    }
    if(columnData.chartColumnType === 'Legend') {
      const updateColumnData = columnsData.find(column => column.chartColumnType === 'Descriptor');
      const colorKeys = Object.keys(columnData.colors);
      colorKeys.forEach(colorKey => {
        if(updateColumnData.colors[colorKey] !== undefined) {
          updateColumnData.colors[colorKey] = columnData.colors[colorKey];
        }
      });
      return [...columnsData];
    }
    if(columnData.chartColumnType === 'SourceGroup') {
      const updateColumnData = columnsData.find(column => column.chartColumnType === 'TargetGroup');
      const colorKeys = Object.keys(columnData.colors);
      colorKeys.forEach(colorKey => {
        if(updateColumnData.colors[colorKey] !== undefined) {
          updateColumnData.colors[colorKey] = columnData.colors[colorKey];
        }
      });
      return [...columnsData];
    }
    if(columnData.chartColumnType === 'TargetGroup') {
      const updateColumnData = columnsData.find(column => column.chartColumnType === 'SourceGroup');
      const colorKeys = Object.keys(columnData.colors);
      colorKeys.forEach(colorKey => {
        if(updateColumnData.colors[colorKey] !== undefined) {
          updateColumnData.colors[colorKey] = columnData.colors[colorKey];
        }
      });
      return [...columnsData];
    }
    return columnsData;
  }

  rollBackChartColors(): void {
    const charts = this.charts$.getValue();
    const selectedChart = this.selectedChart$.getValue();
    const foundedIndex = charts.findIndex(tempChart => tempChart.chartId === selectedChart.chartId);
    selectedChart.columnsData[this.previousSelectedChartColors.columnDataIndex]
      .colors = this.previousSelectedChartColors.colors;
    if(foundedIndex > -1) {
      charts[foundedIndex] = {...selectedChart};
      this.updateCharts(charts);
    }
    this.selectedChart$.next({...selectedChart});
    this.previousSelectedChartColors = null;
  }

  // controller
  updateControllersValuesState(controller: Partial<Controller>): void {
    const updateControllers = this.controllers$.getValue() ?? [];
    const foundedIndex = updateControllers.findIndex
    (tempController => tempController.controllerValue.controllerId === controller.controllerValue.controllerId);
    if(foundedIndex > -1) {
      this.previousSelectedControllerValue = {...updateControllers[foundedIndex]};
      updateControllers.splice(foundedIndex, 1);
    }

    if(controller) {
      controller.isValidController = this.isValidController(controller);
      controller.shoudLoadData = this.shouldLoadData(controller.controllerValue,
          this.previousSelectedControllerValue.controllerValue);
      this.previousSelectedControllerValue = {};
      updateControllers.push(controller);
    }
    this.selectedControllerValue = controller;
    this.controllers$.next([...updateControllers]);
    this.setControllersFilters();
  }

  getControllers$(): Observable<Partial<Controller>[]> {
    return this.controllers$.asObservable();
  }

  getControllersValue(): Partial<Controller>[] {
    return this.controllers$.getValue();
  }

  rollBackChartsControllersValues(): void {
    this.rollBackControllers.forEach(controller => {
      this.updateChartsController(controller);
    });
    this.controllers$.next([...this.rollBackControllers]);
    this.setControllersFilters();
    this.rollBackControllers = null;
  }

  setRollBackControllersValues(): void {
    this.rollBackControllers = this.controllers$.getValue() != null ? [...this.controllers$.getValue()] : [];
    const charts = this.charts$.getValue();
    charts.forEach((chart,index,self) => {
      self[index].controllersValues = [];
    });
    this.updateCharts([...charts]);
    this.controllers$.next([]);
    this.controllersFilters$.next([]);
  }

  updateChartsController(controller: Partial<Controller>): void {
    const charts = this.charts$.getValue();
    if(charts?.length > 0) {
      charts.forEach((chart,index,self) => {
        self[index] = this.updateSingleChartControllersValues(chart,controller);
      });
      this.updateCharts([...charts]);
    }
  }

  deleteChartsControllerValue(controllerId: string): void{
    const charts = this.charts$.getValue();
    let updateControllers = this.controllers$.getValue();
   this.isDeletedController = false;
    if(Array.isArray(charts) && charts.length > 0) {
      charts.forEach((chart,index,self) => {
        if(chart?.controllersValues?.length > 0) {
          self[index].controllersValues =
            chart.controllersValues.filter(controller => controller.controllerId !== controllerId);
        }
      });
    }
    this.updateCharts([...charts]);
    if(updateControllers) {
      const controllerIndex = updateControllers
          .findIndex(controller => controller.controllerValue.controllerId === controllerId);
      this.isDeletedController = updateControllers
          .findIndex(controller => controller.controllerValue.controllerId === controllerId) > -1 &&
          this.isValidController(updateControllers[controllerIndex]);
      updateControllers = updateControllers.filter(controller => controller.controllerValue.controllerId !== controllerId);
      this.controllers$.next([...updateControllers]);
      this.setControllersFilters();
    }
  }

  updateSingleChartControllersValues(chart: Partial<ChartModel>,controller: Partial<Controller>): Partial<ChartModel> {
    let currentControllersValues = [];
    if(chart?.controllersValues?.length > 0) {
      currentControllersValues = [...chart.controllersValues];
    }
    const foundedIndex = currentControllersValues?.findIndex
    (controllerValue => controller.controllerValue.controllerId === controllerValue.controllerId);
    if(foundedIndex > -1) {
      currentControllersValues.splice(foundedIndex, 1);
    }

    if(controller && this.isValidController(controller) &&
      this.isChartInteractWithThisController(chart.chartId,controller)) {
      currentControllersValues.push(controller.controllerValue);
    }
    chart.controllersValues = currentControllersValues;
    return {...chart};
  }

  updateDefaultControllerValues(chart: Partial<ChartModel>) {
    const controller: Partial<Controller> = this.createDefaultController(chart);
    this.updateChartsController(controller);
    this.updateControllersValuesState(controller);
  }

  getDefaultControllersValues(charts: Partial<ChartModel>[],
                              currentControllers: Partial<Controller>[]): Partial<Controller>[] {
    const controllers: Partial<Controller>[] =[];
    if(Array.isArray(charts)) {
      charts.forEach(chart => {
        const hasValidControllerDefaultValues = chart.isController && chart.defaultValuesForController?.defaultValues &&
            Array.isArray(chart.defaultValuesForController.defaultValues)  &&
            chart.defaultValuesForController.defaultValues.length > 0 && currentControllers
                .filter(tempController => tempController.controllerValue.controllerId === chart.chartId).length === 0;
        if(hasValidControllerDefaultValues) {
          const controller: Partial<Controller> = this.createDefaultController(chart);
          if(controller) {
            controllers.push(controller);
          }
        }
      });
    }
    return controllers;
  }

  createDefaultController(chart: Partial<ChartModel>): Partial<Controller> {
    if(chart.chartDataSettings.descriptorColumns?.length > 0) {
      const controller: Partial<Controller> = {
        controllerValue: {
          controllerId: chart.chartId,
          dataSourceId: chart.chartDataSettings.descriptorColumns[0].dataSourceId,
          columnId: chart.chartDataSettings.descriptorColumns[0].columnId,
        },
        isInteractableWithCharts: chart.isInteractableWithCharts,
        otherChartsInteractionStatusWithThis: chart.otherChartsInteractionStatusWithThis,
      };
      if(chart.chartType === 'DropdownController') {
        controller.controllerValue.controllerType = 'DropdownController';
        if(chart?.chartDesignSettings.chart.singleSelect){
          controller.controllerValue.selectedValues = chart.defaultValuesForController.defaultValues.length?[chart.defaultValuesForController.defaultValues[0]]:[];
        }else{
          controller.controllerValue.selectedValues = chart.defaultValuesForController.defaultValues;
        }
        controller.controllerValue.isInclude = !chart?.chartDesignSettings.chart.isExcluded;
        controller.isValidController = this.isValidController(controller);
        return controller;
      } else if (chart.chartType === 'DateRangeController') {
        controller.controllerValue.controllerType = 'DataRangeController';
        controller.controllerValue.dateControllerType = chart.defaultValuesForController.defaultValueType;
        controller.controllerValue.startDate = chart.defaultValuesForController.defaultValues[0];
        controller.controllerValue.endDate = chart.defaultValuesForController.defaultValues[1];
        if(chart.defaultValuesForController.defaultValueType === 'AdvancedRange') {
          controller.controllerValue.advancedRangeDate = {...chart.defaultValuesForController.defaultValueAdvancedRange};
        }
        if(chart.defaultValuesForController.defaultValueType === 'AdvancedSingle') {
          controller.controllerValue.advancedSingleDate = {...chart.defaultValuesForController.defaultValueAdvancedSingle};
        }
        if(chart.defaultValuesForController.defaultValueType === 'Relative') {
          controller.controllerValue.relativeRangeDate = {...chart.defaultValuesForController.defaultValueRelative};
        }
        controller.isValidController = this.isValidController(controller);
        return controller;
      } else if (chart.chartType === 'DateListController') {
        controller.controllerValue.controllerType = 'DateListController';
        controller.controllerValue.startDate = chart.defaultValuesForController.defaultValues[0];
        controller.controllerValue.endDate = chart.defaultValuesForController.defaultValues[1];
        controller.controllerValue.calenderType = chart.defaultValuesForController.defaultValueDateList?.calenderType;
        controller.controllerValue.selectedDateList =
          chart.defaultValuesForController.defaultValueDateList?.defaultValueDateList;
        if (controller.controllerValue.selectedDateList === 'None') {
          controller.controllerValue.selectedDateList = undefined;
        }
        controller.isValidController = this.isValidController(controller);
        return controller;
      } else if (chart.chartType === 'SliderController') {
        controller.controllerValue.controllerType = 'SliderController';
          controller.controllerValue.singleVal = undefined;
          controller.controllerValue.minVal = undefined;
          controller.controllerValue.maxVal = undefined;
        if(chart.sliderControllerProperties.sliderControllerType === 'IntervalValues') {
          controller.controllerValue.minVal = chart.defaultValuesForController.defaultValues[0];
          controller.controllerValue.maxVal = chart.defaultValuesForController.defaultValues[1];
        } else if (chart.sliderControllerProperties.sliderControllerType === 'FixedMaximum'){
          controller.controllerValue.minVal = chart.defaultValuesForController.defaultValues[0];
        } else if (chart.sliderControllerProperties.sliderControllerType === 'FixedMinimum') {
          controller.controllerValue.maxVal = chart.defaultValuesForController.defaultValues[0];
        } else {
          controller.controllerValue.singleVal = chart.defaultValuesForController.defaultValues[0];
        }
        controller.controllerValue.sliderControllerType = chart.sliderControllerProperties.sliderControllerType;
        controller.isValidController = this.isValidController(controller);
        return controller;
      }
    }
    return undefined;
  }

  isChartInteractWithThisController(chartId: string, controller: Partial<Controller>) {
    if(controller.isInteractableWithCharts) {
      const chart = controller.otherChartsInteractionStatusWithThis.find(chartInteract => chartInteract.chartId === chartId);
      return chart === undefined || chart?.interactStatusType !== 'NoInteraction';
    }
    return true;
  }

  isValidController(controller: Partial<Controller>): boolean {
    return (controller.controllerValue.controllerType === 'AdvancedQualityFilterController' &&
      controller.controllerValue.advancedQualityValue !== '') ||
      (controller.controllerValue.controllerType === 'AdvancedQuantityFilterController' &&
        controller.controllerValue.advancedQuantityValue !== '') ||
        this.validationDateController(controller.controllerValue) ||
      (controller.controllerValue.controllerType === 'DropdownController' &&
          controller.controllerValue.selectedValues?.length > 0) ||
      (controller.controllerValue.controllerType === 'DateListController' &&
        controller.controllerValue.startDate?.length > 0) ||
      this.isValidSlicerController(controller.controllerValue);
  }

  validationDateController(controllerValue: Partial<ControllerValue>): boolean {
    if (controllerValue.controllerType !== 'DataRangeController') {
      return false;
    }
    if (controllerValue.dateControllerType === 'AdvancedSingle') {
      return this.isValidAdvanceSingleDateController(controllerValue.advancedSingleDate);
    } else if (controllerValue.dateControllerType === 'AdvancedRange') {
        return this.isValidAdvanceRangeDateController(controllerValue.advancedRangeDate);
    }else if (controllerValue.dateControllerType === 'Relative') {
      return this.isValidRelativeRangeDateController(controllerValue.relativeRangeDate);
    }
    return this.isValidDateRangeController(controllerValue);
  }

  isValidSlicerController(controllerValue: Partial<ControllerValue>): boolean {
    if(controllerValue.controllerType !== 'SliderController') {
      return false;
    }
    if(controllerValue.sliderControllerType === 'IntervalValues') {
      return controllerValue.minVal != null || controllerValue.maxVal != null;
    }
    if(controllerValue.sliderControllerType === 'FixedMaximum') {
      return controllerValue.minVal != null;
    }
    if(controllerValue.sliderControllerType === 'FixedMinimum') {
      return controllerValue.maxVal != null;
    }
    return controllerValue.singleVal != null;
  }

  isValidAdvanceSingleDateController(advanceSingleDate: AdvanceSingleDate): boolean {
    if(advanceSingleDate == null) {
      return false;
    }
    const rangeParameterDateCondition = ((advanceSingleDate.rangeParameterDate === 'BeforeFrom' ||
                advanceSingleDate.rangeParameterDate === 'AfterFrom') &&
            advanceSingleDate.date != null) ||
        advanceSingleDate.rangeParameterDate === 'Before' ||
        advanceSingleDate.rangeParameterDate === 'After';
    return advanceSingleDate.timeScope != null &&
        advanceSingleDate.timeScopeValue != null && rangeParameterDateCondition;
  }

  isValidAdvanceRangeDateController(advanceRangeDate: AdvanceRangeDate): boolean {
    if(advanceRangeDate == null) {
      return false;
    }
    const rangeParameterDateCondition = (advanceRangeDate.secondParameterDate === 'Current' || (
        advanceRangeDate.secondParameterDate === 'DateRange' &&
        advanceRangeDate.timeScopeValue != null &&
        advanceRangeDate.secondTimeScope != null));
    return advanceRangeDate.firstParameterDate != null &&
        advanceRangeDate.firstTimeScope != null && rangeParameterDateCondition;
  }

  isValidRelativeRangeDateController(relativeRangeDate: RelativeRangeDate): boolean {
    if (relativeRangeDate == null) {
      return false;
    }
    const { parameterDate, parameterDateValue, scope, scopeValue, timeMode } = relativeRangeDate;
    const hasRequiredFields = parameterDate != null &&
                              scope != null &&
                              timeMode != null;

    const parameterDateCondition = parameterDate === "AnyDate"
      ? parameterDateValue != null
      : true;

    const scopeValueCondition = (timeMode === "Past" || timeMode === "Future")
      ? scopeValue != null
      : true

    const isValid = hasRequiredFields && parameterDateCondition && scopeValueCondition;
    return isValid;
  }

  isValidDateRangeController(controllerValue: Partial<ControllerValue>): boolean {
    return controllerValue.dateControllerType === 'DateRange' &&
    (controllerValue.startDate != null || controllerValue.endDate != null);
  }

  shouldLoadData(newControllerValue: Partial<ControllerValue>, previousControllerValue: Partial<ControllerValue>): boolean {
    if(newControllerValue.controllerType !== 'DataRangeController' &&
        newControllerValue.controllerType !== 'SliderController') {
      return true;
    }
    if(newControllerValue.controllerType === 'DataRangeController') {
      if(previousControllerValue?.controllerId === newControllerValue.controllerId &&
          this.validationDateController(previousControllerValue) !== this.validationDateController(newControllerValue)) {
        return true;
      }
      return this.validationDateController(newControllerValue);
    }
    if(newControllerValue.controllerType === 'SliderController') {
      if(previousControllerValue?.controllerId === newControllerValue.controllerId &&
          this.isValidSlicerController(previousControllerValue) !== this.isValidSlicerController(newControllerValue)) {
        return true;
      }
      return this.isValidSlicerController(newControllerValue);
    }
  }

  controllersShouldLoadChartsData(): boolean {
    const controllers = this.controllers$.getValue();
    const shouldLoadData = controllers
        .reduce((loadData, controller) => loadData || controller.shoudLoadData,
            this.isDeletedController);
    controllers.forEach((controller, index, self)=>
        self[index].shoudLoadData = false);
    this.isDeletedController = false;
    return shouldLoadData;
  }

  //calculate column

  getCalculateColumnMode$(): Observable<CalculateColumnModeType>{
    return this.calculateColumnMode$.asObservable();
  }

  getCalculateColumnError$(): Observable<string> {
    return this.calculateColumnError$.asObservable();
  }

  setCalculateColumnError(error: string): void {
    this.calculateColumnError$.next(error);
  }

  setCalculateColumnMode(calculateColumnMode: CalculateColumnModeType): void {
    if(this.calculateColumnMode$.getValue() !== calculateColumnMode) {
      this.calculateColumnMode$.next(calculateColumnMode);
    }
  }

  getEditCalculateColumn$(): Observable<Partial<CalculateColumnModel>> {
    return this.editCalculateColumn$.asObservable();
  }

  setEditCalculateColumn(editCalculateColumn: Partial<CalculateColumnModel>): void {
    this.editCalculateColumn$.next(editCalculateColumn);
  }

  getCalculateColumnSubmit$(): Observable<SubmitType> {
    return this.calculateColumnSubmit$.asObservable();
  }

  setCalculateColumnSubmit(submit: SubmitType): void {
    this.calculateColumnSubmit$.next(submit);
  }

  addCalculateColumnToDataSource(column: DashboardColumn, dataSourceId: string){
    let dataSources = this.dataSourcesOfDashboard$.getValue();
    const parameters = this.parametersOfDashboard$.getValue();
    const dataSourceIndex = dataSources.findIndex(d => d.dataSourceId === dataSourceId);
    if(dataSourceIndex > -1){
      dataSources[dataSourceIndex].dashboardColumns.push(column);
      dataSources = dataSources.concat(parameters);
      this.updateDataSourcesOfDashboard([...dataSources]);
    }
  }

  updateCalculateColumnInDataSource(column: DashboardColumn, dataSourceId: string){
    const dataSources = this.dataSourcesOfDashboard$.getValue();
    const dataSourceIndex = dataSources.findIndex(d => d.dataSourceId === dataSourceId);
    if(dataSourceIndex > -1) {
      const columnIndex = dataSources[dataSourceIndex].dashboardColumns.findIndex(c => c.columnId === column.columnId);
      if(columnIndex > -1){
        dataSources[dataSourceIndex].dashboardColumns[columnIndex] = column;
        const parameters = this.parametersOfDashboard$.getValue();
        this.updateDataSourcesOfDashboard([...dataSources,...parameters]);
      }
    }
  }

  // interaction

  updateInteractionValues(newInteractionValues: InteractionValues[]) {
    this.interactionValues$.next([...newInteractionValues]);
  }

  getValidInteractionValues(chart, interactionValues: InteractionValues[]): InteractionValues[] {
    const sourceChart = this.interactSourceChart$.getValue();
    return interactionValues?.length > 0 && chart.chartId !== sourceChart?.chartId ? interactionValues : undefined;
  }

  getInteractionValues$(): Observable<InteractionValues[]> {
    return this.interactionValues$.asObservable();
  }

  toggleEditInteractionMode(): void {
    const editInteractionMode = this.editInteractionMode$.getValue();
    if(!editInteractionMode) {
      this.setRollBackControllersValues();
    }
    this.editInteractionMode$.next(!editInteractionMode);
  }

  getEditInteractionMode$(): Observable<boolean> {
    return this.editInteractionMode$.asObservable();
  }

  setSelectedInteractionChart(chart: Partial<ChartModel>) {
    this.selectedInteractionChart$.next({...chart});
  }

  getSelectedInteractionChart$(): Observable<Partial<ChartModel>> {
    return this.selectedInteractionChart$.asObservable();
  }

  getSelectedInteractionChartValue(): Partial<ChartModel> {
    return this.selectedInteractionChart$.getValue();
  }

  updateChartInteractAbility(chart: Partial<ChartModel>, isInteractable: boolean): void {
    const charts = this.charts$.getValue();
    const controllers =this.controllers$.getValue() ?? [];
    const selectedChart = this.selectedChart$.getValue();
    let foundedIndex = charts.findIndex(tempChart => tempChart.chartId === tempChart.chartId);
    this.updateChartInteractAbilityStatus = chart.isController && selectedChart.isInteractableWithCharts !== isInteractable;
    selectedChart.isInteractableWithCharts = isInteractable;
    this.selectedChart$.next({...selectedChart});
    if(foundedIndex > -1) {
      charts[foundedIndex].isInteractableWithCharts = isInteractable;
      if(chart.isController) {
        foundedIndex = controllers.findIndex(controller => controller.controllerValue.controllerId === chart.chartId);
        if(foundedIndex > -1) {
          controllers[foundedIndex].isInteractableWithCharts = isInteractable;
          charts.forEach((chartTemp,index,self) => {
            chartTemp.controllersValues = [];
            controllers.forEach(controller => self[index] = this.updateSingleChartControllersValues(chartTemp,controller));
          });
        }
        this.updateCharts([...charts]);
        this.controllers$.next(controllers);
        this.setControllersFilters();
      } else {
        this.updateCharts([...charts]);
      }
    }
  }

  updateOtherChartsInteractionStatusWithSourceChart(chartId: string, selectedInteraction: ChartInteractionType) {
    const interactionSourceChart = {...this.selectedInteractionChart$.getValue()};
    const foundedIndex = interactionSourceChart.otherChartsInteractionStatusWithThis
      .findIndex(status => status.chartId === chartId);
    interactionSourceChart.otherChartsInteractionStatusWithThis =
      [...interactionSourceChart.otherChartsInteractionStatusWithThis];
    if(foundedIndex > -1) {
      const interactionStatus = {...interactionSourceChart.otherChartsInteractionStatusWithThis[foundedIndex]};
      interactionStatus.interactStatusType = selectedInteraction;
      interactionSourceChart.otherChartsInteractionStatusWithThis[foundedIndex] = interactionStatus;
    } else {
      interactionSourceChart.otherChartsInteractionStatusWithThis.push({
        dashboardId: this.dashboardId,
        chartId,
        interactStatusType: selectedInteraction,
      });
    }
    this.selectedInteractionChart$.next(interactionSourceChart);
  }

  updateChartInteractionWithOtherCharts(sourceChart: Partial<ChartModel>): void {
    let charts = this.charts$.getValue();
    const controllers = this.rollBackControllers;
    let foundedIndex = charts.findIndex(chart => chart.chartId === sourceChart.chartId);
    if(foundedIndex > -1) {
      charts[foundedIndex] = {...sourceChart};
      if(sourceChart.isController) {
        foundedIndex = controllers.findIndex(controller => controller.controllerValue.controllerId === sourceChart.chartId);
        if(foundedIndex > -1) {
          controllers[foundedIndex].otherChartsInteractionStatusWithThis = sourceChart.otherChartsInteractionStatusWithThis;
        }
      }
      charts = this.setChartsControllersValues(charts, controllers);
      this.updateCharts([...charts]);
      this.controllers$.next([...controllers]);
      this.setControllersFilters();
      this.rollBackControllers = null;
    }
    this.selectedChart$.next({...sourceChart});
  }

  setChartsControllersValues(charts: Partial<ChartModel>[], controllers: Partial<Controller>[]): Partial<ChartModel>[] {
    if(!Array.isArray(charts)) {
      return [];
    }
    if(!Array.isArray(controllers)) {
      return charts;
    }
    charts.forEach((chart,index,self) => {
      controllers.forEach(controller => {
        self[index] = this.updateSingleChartControllersValues(chart, controller);
      });
    });
    return charts;
  }

  toggleInteractionMode(chart: Partial<ChartModel>, interactionMode: boolean): void {
    this.interactionMode$.next(interactionMode);
    if(!interactionMode) {
      this.interactionValues$.next([]);
      this.interactSourceChart$.next(null);
    } else {
      this.interactSourceChart$.next({...chart});
    }
  }

  getInteractionMode$(): Observable<boolean> {
    return this.interactionMode$.asObservable();
  }

  getInteractionModeValue(): boolean {
    return this.interactionMode$.getValue();
  }

  getInteractSourceChart$(): Observable<Partial<ChartModel>> {
    return this.interactSourceChart$.asObservable();
  }

  getChartsInteractionWithSourceChart(charts: InteractionStatus[]): Partial<ChartModel>[] {
    const interactionValuesChartIdList = this.interactionValues$.getValue().map(item=>item.interactedChartId);
    const dashboardCharts = this.charts$.getValue();
    const interactionSourceChart = this.interactSourceChart$.getValue();
    const chartsInteractionWithSourceChart = [];
    dashboardCharts.forEach((dashboardChart) => {
      const foundedChartIndex = charts.findIndex(chart => dashboardChart.chartId === chart.chartId);
      if((foundedChartIndex > -1 && charts[foundedChartIndex].interactStatusType !== 'NoInteraction') ||
        (foundedChartIndex === -1 && dashboardChart.chartId !== interactionSourceChart.chartId) ) {
        chartsInteractionWithSourceChart.push(dashboardChart);
      }
    });
    return chartsInteractionWithSourceChart.filter(item=>{
      return interactionValuesChartIdList.indexOf(item.chartId)===-1
    });
  }

  updateInteractionCharts(otherChartsInteractionStatusWithThis: InteractionStatus[], interactionMode: boolean, shouldResetAllCharts?:boolean): void {
    let charts: Partial<ChartModel>[] = this.interactionCharts$.getValue();
    if(!interactionMode) {
      this.interactionValues$.next([]);
      if(shouldResetAllCharts){
        charts = otherChartsInteractionStatusWithThis;
      }
    } else {
      charts = this.getChartsInteractionWithSourceChart(otherChartsInteractionStatusWithThis);
    }
    this.resetChartsZoomStatus(charts);
    this.interactionCharts$.next([...charts]);
  }

  updateInteractionChartsInDrillDown(currentChart: Partial<ChartModel>): void {
    let charts: Partial<ChartModel>[] = this.interactionCharts$.getValue();
    if(this.interactionMode$.getValue()) {
      charts = charts.filter(chart => chart.chartId !== currentChart.chartId);
      this.interactionValues$.next([]);
      this.interactionCharts$.next([...charts]);
    }
  }

  getInteractionCharts$(): Observable<Partial<ChartModel>[]> {
    return this.interactionCharts$.asObservable();
  }

  //Drilling
  setInitialChartsDrillDownMode(chartsLength: number): void {
    this.chartsDrillDownMode$.next(Array(chartsLength).fill(false));
  }

  updateChartDrillDownMode(chartIndex: number): void {
    const updateChartsDrillDownMode = this.chartsDrillDownMode$.getValue();
    if(updateChartsDrillDownMode.length > chartIndex) {
      updateChartsDrillDownMode[chartIndex] = !updateChartsDrillDownMode[chartIndex];
      this.chartsDrillDownMode$.next([...updateChartsDrillDownMode]);
    }
  }

  getChartsDrillDownMode$(): Observable<boolean[]> {
    return this.chartsDrillDownMode$.asObservable();
  }

  resetChartDrillingProperties(chart: Partial<ChartModel>): Partial<ChartModel> {
    if(chart?.drillingProperties !== undefined && chart.drillingProperties.drillLevel > 1) {
      chart.drillingProperties = {
        drillingType: ['None'],
        drillDownRealColumns: [],
        drillDownShowColumns: [],
        drillLevel: 1,

      };
      const updateChartsDrillDownValues = this.chartsDrillDownValues$.getValue();
      if(updateChartsDrillDownValues.length > chart.chartIndexInDashboard) {
        updateChartsDrillDownValues[chart.chartIndexInDashboard] = [];
        this.chartsDrillDownValues$.next(updateChartsDrillDownValues);
      }
    }
    return chart;
  }

  setDrillThroughActivation(activated: boolean): void {
    this.drillThroughActivated$.next(activated);
  }

  getDrillThroughActivation$(): Observable<boolean> {
    return this.drillThroughActivated$.asObservable();
  }

  updateDrillThroughChart(chart: Partial<ChartModel>): void {
    this.drillThroughChart$.next(chart);
  }

  getDrillThroughChart$(): Observable<Partial<ChartModel>> {
    return this.drillThroughChart$.asObservable();
  }

  toggleIsDrillThrough(): void {
    this.isDrillThrough$.next(!this.isDrillThrough$.getValue());
  }

  checkDrillThroughStates(): void {
    const isDrillThrough = this.isDrillThrough$.getValue();
    const previousDrillThroughState = this.drillThroughState$.getValue();
    let nextDrillThroughState;
    if(isDrillThrough) {
      if(previousDrillThroughState === 'None') {
        this.drillThroughState$.next('DrillThrough');
        nextDrillThroughState = 'DrillThrough';
      }
      if(previousDrillThroughState === 'DrillThrough') {
        this.drillThroughState$.next('MultiDrillThrough');
        nextDrillThroughState = 'MultiDrillThrough';
      }
      this.toggleIsDrillThrough();
    } else {
      this.drillThroughState$.next('None');
      nextDrillThroughState = 'None';
    }
    if(nextDrillThroughState !== 'None') {
      this.drillThroughFiltersState = 'GetDrillThroughFiltersDestinationDashboard';
    } else if(previousDrillThroughState !== 'None' && nextDrillThroughState === 'None') {
      this.drillThroughFiltersState ='GetDrillThroughFiltersSourceDashboard';
    } else {
      this.drillThroughFiltersState ='None';
    }
    this.setDrillThroughActivation(false);
  }

  getDrillThroughState$(): Observable<DrillThroughStateType> {
    return this.drillThroughState$.asObservable();
  }

  setRoutingDrillThroughState(routeType: RouteType, workspaceId: string, dashboardId: string): void {
    this.routingDrillThroughState$.next({routeType,workspaceId,dashboardId});
  }

  getRoutingDrillThroughState$(): Observable<RoutingDrillThrough> {
    return this.routingDrillThroughState$.asObservable().pipe(skip(1));
  }

  updateChartDrillThroughProperties(chartId: string, drillThroughProperties: DrillThroughProperties): void {
    const charts = this.charts$.getValue();
    const selectedChart = this.selectedChart$.getValue();
    const foundedIndex = charts.findIndex(tempChart => tempChart.chartId === chartId);
    if(foundedIndex > -1) {
      charts[foundedIndex].interactionWithPagesProperties = drillThroughProperties;
      this.updateCharts(charts);
    }
    selectedChart.interactionWithPagesProperties = drillThroughProperties;
    this.selectedChart$.next({...selectedChart});
  }

  addDrillThroughSourceDashboard(sourceDashboardId: string,sourceChartId: string): void {
    this.drillThroughSourceDashboard$.next({sourceDashboardId,sourceChartId});
  }

  deleteDrillThroughSourceDashboard(): void {
    this.drillThroughSourceDashboard$.next(null);
  }

  getDrillThroughSourceDashboardValue(): DrillThroughSourceDashboard {
    return this.drillThroughSourceDashboard$.getValue();
  }

  getDrillThroughSourceDashboard$(): Observable<DrillThroughSourceDashboard> {
    return this.drillThroughSourceDashboard$.asObservable();
  }

  updateDashboardUsedInDrillThrough(dashboard: DashboardDrillThrough): void {
    this.dashboardUsedInDrillThrough$.next(dashboard);
  }

  getDashboardUsedInDrillThrough$(): Observable<DashboardDrillThrough> {
    return this.dashboardUsedInDrillThrough$.asObservable().pipe(skip(1));
  }

  updateDashboardsForDrillThrough(dashboards: DashboardDrillThrough[]): void {
    this.dashboardsForDrillThrough$.next(dashboards);
  }

  getDashboardsForDrillThrough$(): Observable<DashboardDrillThrough[]> {
    return this.dashboardsForDrillThrough$.asObservable().pipe(skip(1));
  }

  setDrillThroughFilters(controllersValues: Partial<ControllerValue>[],isDestinationDashboard: boolean): void {
    const drillThroughFilters: DashboardControllersFilter[] = [];
    const updateControllers: Partial<Controller>[] =[];
    if(Array.isArray(controllersValues)) {
      controllersValues.forEach((controllerValue,index,selfControllers) => {
        if(isDestinationDashboard) {
          drillThroughFilters.push(this.dashboardControllersFilter(controllerValue));
          updateControllers.push({controllerValue , isValidController:this.isValidController({controllerValue})});
        }
        if(!isDestinationDashboard && !controllerValue.selectedFieldFilter) {
          updateControllers.push({ controllerValue, isValidController: this.isValidController({ controllerValue })});
        }
      });
      if(isDestinationDashboard) {
        this.sourceDashboardControllers = updateControllers;
      } else {
        this.sourceDashboardControllers = [];
        this.controllers$.next([...updateControllers]);
      }
      this.drillThroughFilters$.next(drillThroughFilters);
    }
  }

  setDrillThroughChartFilters(chartFilters): void {
    const drillThroughFilters: DashboardControllersFilter[] = [];
    if(Array.isArray(chartFilters)) {
      chartFilters.forEach((controllerValue,index,selfControllers) => {
          drillThroughFilters.push(this.dashboardChartsFilter(controllerValue));
      });
      this.chartFilters$.next(chartFilters);
      this.drillThroughChartFilters$.next(drillThroughFilters);
    }
  }

  dashboardChartsFilter(chartFilterValue: ChartFilterValue): DashboardControllersFilter {
    if (!chartFilterValue) {
      return undefined;
    }
    const filter: DashboardControllersFilter = {
      name: chartFilterValue.columnName || chartFilterValue.filterType,
      value:'',
      showValue: '',
      controllerId: chartFilterValue.chartId,
      isIncluded: chartFilterValue.filterType === 'AdvancedQuantity' ||
      chartFilterValue.filterType === 'TopNQuantity' ||
      chartFilterValue.filterType === 'AdvancedQuality' ||
      chartFilterValue.filterType === 'TopNQuality'
      ? true
      : chartFilterValue.isInclude,
    };
    if(chartFilterValue.filterType === 'Basic') {
      filter.value = chartFilterValue.selectedValues.join(',');
    } else if(chartFilterValue.filterType === 'Date') {
      filter.value = `${chartFilterValue.startDate} تا ${chartFilterValue.endDate}`;
    } else if(chartFilterValue.filterType === 'DateList') {
      filter.value = this.convertSelectedDateListValue(chartFilterValue.defaultValueDateList);
    } else if (chartFilterValue.filterType === 'AdvancedQuality') {
      let qualityValue = '';
      chartFilterValue.advancedQualityValues?.forEach(_quality => {
        qualityValue += this.convertAdvancedQualityFilter(_quality.key, _quality.value);
      });
      filter.value = qualityValue;
    } else if (chartFilterValue.filterType === 'AdvancedQuantity') {
      let quantityValue = '';
      chartFilterValue.advancedQuantityValues?.forEach((_quantity, index) => {
        quantityValue += this.convertAdvancedQuantityFilter(_quantity.key, _quantity.value);
        if(index === 0) {
          quantityValue += chartFilterValue.advancedQuantityOperatorsValues[0] ? ' و ' : ' یا ';
        }
      });
      filter.value = quantityValue;
    } else if(chartFilterValue.filterType === 'TopNQuality' || chartFilterValue.filterType === 'TopNQuantity') {
      filter.value = this.convertTopNQualityFilter(chartFilterValue.topNShowDirection, chartFilterValue.topNValue);
    }
    return filter;
  }

  getDrillThroughChartFilters() {
    return this.drillThroughChartFilters$.value;
  }

  getDrillThroughChartFilters$() {
    // return this.drillThroughChartFilters$.asObservable();
    return this.chartFilters$.asObservable();
  }

  getChartFilterForDisplay$() {
    return this.drillThroughChartFilters$.asObservable();
  }

  deleteDrillThroughFilter(controllerId: string): boolean {
    const charts = this.charts$.getValue();
    let drillThroughFilters = this.drillThroughFilters$.getValue();
    const foundedFilter = drillThroughFilters.findIndex(filter => filter.controllerId === controllerId);
    charts.forEach((chart,index,self) => {
      if(Array.isArray(chart.controllersValues)) {
        self[index].controllersValues = chart.controllersValues.filter(controllerValue =>
            controllerValue.controllerId !== controllerId);
      }
    });
    drillThroughFilters = drillThroughFilters.filter(filter => filter.controllerId !== controllerId);
    if(Array.isArray(this.sourceDashboardControllers)) {
      this.sourceDashboardControllers = this.sourceDashboardControllers.
      filter(controller => controller.controllerValue.controllerId !== controllerId);
    }
    this.updateCharts([...charts]);
    this.drillThroughFilters$.next([...drillThroughFilters]);
    return foundedFilter > -1;
  }

  getDrillThroughFilters$(): Observable<DashboardControllersFilter[]> {
    return this.drillThroughFilters$.asObservable();
  }

  getControllersForDuringDrillThrough(selectedFieldValues: Partial<ControllerValue>[],
                                      isAllFilter: boolean = false): Partial<ControllerValue>[] {
    let result = [];
    if(isAllFilter) {
      const controllers = this.controllers$.getValue() ?? [];
      controllers.forEach(controller => {
        controller.controllerValue.selectedFieldFilter = false;
        if(controller.controllerValue.controllerType === 'DataRangeController') {
          controller.controllerValue.calenderType = this.getCalendarType(controller.controllerValue);
        }
        result.push(controller.controllerValue);
      });
    }
    if(Array.isArray(selectedFieldValues) && selectedFieldValues.length > 0) {
      result = result.concat(selectedFieldValues);
    }
    return result;
  }

  getCalendarType(controllerValue: Partial<ControllerValue>): CalenderType {
    const chart = this.charts$.getValue().find(chartTemp => chartTemp.chartId === controllerValue.controllerId);
    if(!chart) {
      return undefined;
    }
    if(!Array.isArray(chart.chartDataSettings.descriptorColumns) ||
        chart.chartDataSettings.descriptorColumns.length === 0) {
      return undefined;
    }
    return chart.chartDataSettings.descriptorColumns[0].formatShow.calenderType;
  }

  setControllersFilters(): void {
    const controllersFilters: DashboardControllersFilter[] = [];
    const controllers = this.controllers$.getValue();

    if (Array.isArray(controllers)) {
      controllers.forEach((controller) => {
        if (controller.isValidController) {
          controllersFilters.push(this.dashboardControllersFilter(controller.controllerValue));
        }
      });
      this.controllersFilters$.next(controllersFilters);
    } else {
      this.controllersFilters$.next([]);
    }
  }

  getControllersFilters$(): Observable<DashboardControllersFilter[]> {
    return this.controllersFilters$.asObservable();
  }

  private prepareSelectedChartFilterValues(selectedChartControllerValue){
    let selectedValue: any = selectedChartControllerValue.selectedValues;
    const selectedChart = this.getChartValue(selectedChartControllerValue.controllerId);
    if(selectedChart?.columnsData?.length){
      if(Array.isArray(selectedValue) && selectedValue.length){
        const diffValues = _.difference(selectedValue as any,selectedChart.columnsData[0].data) || [];
        if(diffValues.length) {
          diffValues.forEach((item:any) => {
            selectedChart.columnsData[0].data.push(item)
          });
        }
        selectedValue = [...selectedValue, ...diffValues];
        // selectedValue = _.intersection(selectedValue as any,selectedChart.columnsData[0].data);
        // selectedValue = _.intersection(selectedValue as any,selectedChart.columnsData[0].data);
      }else if(selectedValue){
        const itemIndex = selectedChart.columnsData[0].data.findIndex(item=>item===selectedValue);
        selectedValue = itemIndex!==-1?selectedChart.columnsData[0].data[itemIndex]:undefined;
      }else{
        selectedValue = undefined;
      }
    }
    return selectedValue;
  }

  dashboardControllersFilter(controllerValue: Partial<ControllerValue>): DashboardControllersFilter {
    if (!controllerValue) {
      return undefined;
    }

    const chart: Partial<ChartModel> = this.charts$.getValue().find(item => item.chartId === controllerValue.controllerId);
    const controllerFilter: DashboardControllersFilter = {
      name: controllerValue.columnName,
      value:'',
      showValue: '',
      controllerId: controllerValue.controllerId,
      isIncluded: controllerValue.isInclude
    };

    if (!controllerValue.columnName &&
        Array.isArray(chart?.chartDataSettings?.descriptorColumns) &&
        chart?.chartDataSettings?.descriptorColumns?.length > 0) {
      controllerFilter.name = chart?.chartDataSettings?.descriptorColumns?.[0]?.name;
    }

    if (controllerValue.controllerType === 'DateListController') {
      controllerFilter.value = this.convertSelectedDateListValue(controllerValue.selectedDateList);
      controllerFilter.isIncluded = true;
    } else if (controllerValue.controllerType === 'DropdownController') {
      // const chartSelectedValues = this.prepareSelectedChartFilterValues(controllerValue)
      const chartSelectedValues = controllerValue.selectedValues
      controllerFilter.originalValues = chartSelectedValues;
      controllerFilter.isIncluded = controllerValue.isInclude
      controllerFilter.value = chartSelectedValues.map(value => {
        if (value == null) {
          return 'null';
        }
        return value;
      }).join(', ');
    } else if (controllerValue.controllerType === 'DataRangeController') {
      const calenderType = chart ?
      chart?.chartDataSettings.descriptorColumns[0].formatShow.calenderType :
      (controllerValue.calenderType ?? undefined);
      let value = '';
      if (controllerValue.startDate != null) {
        if(chart?.chartDesignSettings?.chart?.singleSelect){
          value +=  this.convertDataRangeFilter(controllerValue.startDate, calenderType);
        } else {
          value += ' از ' + this.convertDataRangeFilter(controllerValue.startDate, calenderType);
        }
      }
      if (controllerValue.endDate != null) {
        if (value !== '') {
          value += '  ' + ' تا  ' + this.convertDataRangeFilter(controllerValue.endDate, calenderType);
        } else {
          value += ' تا ' + this.convertDataRangeFilter(controllerValue.endDate, calenderType);
        }
      }
      if(value === '') {
        value = 'null';
      }
      controllerFilter.value = value;
      controllerFilter.isIncluded = true;
    } else if (controllerValue.controllerType === 'AdvancedQualityFilterController') {
      controllerFilter.value = this.convertAdvancedQualityFilter(controllerValue.advancedQualityFilter,
        controllerValue.advancedQualityValue);
      controllerFilter.isIncluded = true;
    } else if (controllerValue.controllerType === 'AdvancedQuantityFilterController') {
      controllerFilter.value = this.convertAdvancedQuantityFilter(controllerValue.advancedQuantityFilter,
        controllerValue.advancedQuantityValue);
      controllerFilter.isIncluded = true;
    } else if (controllerValue.controllerType === 'SliderController') {
      let value = '';
      if (controllerValue?.singleVal != null) {
        value = controllerValue.singleVal?.toString();
      } else if (controllerValue.minVal != null && controllerValue.maxVal != null) {
        value = controllerValue.minVal.toString() + ' تا ' + controllerValue.maxVal.toString();
      } else if (controllerValue.minVal != null) {
        value = 'از ' + controllerValue.minVal.toString();
      } else if (controllerValue.maxVal != null) {
        value = 'تا ' + controllerValue.maxVal.toString();
      } else {
        value = 'null';
      }
      controllerFilter.value = value;
      controllerFilter.isIncluded = true;
    }
    return controllerFilter;
  }


  convertSelectedDateListValue(dateListValue: DateListOptions): string {
    switch (dateListValue) {
      case 'Today':
        return 'امروز';
      case 'Yesterday':
        return 'دیروز';
      case 'From7DaysAgo':
        return 'از ۷ روز پیش';
      case 'From14DaysAgo':
        return 'از ۱۴ روز پیش';
      case 'From28DaysAgo':
        return 'از ۲۸ روز پیش';
      case 'From30DaysAgo':
        return 'از ۳۰ روز پیش';
      case 'CurrentWeek':
        return 'هفته کنونی';
      case 'CurrentWeekToToday':
        return 'از ابتدای هفته کنونی تا امروز';
      case 'PreviousWeek':
        return 'هفته پیش';
      case 'CurrentMonth':
        return 'ماه کنونی';
      case 'CurrentMonthToToday':
        return 'از ابتدای ماه کنونی تا امروز';
      case 'PreviousMonth':
        return 'ماه پیش';
      case 'CurrentSeason':
        return 'فصل کنونی';
      case 'CurrentSeasonToDate':
        return 'از ابتدای فصل کنونی تا امروز';
      case 'PreviousSeason':
        return 'فصل پیش';
      case 'CurrentYear':
        return 'سال کنونی';
      case 'CurrentYearToDate':
        return 'از ابتدای سال کنونی تا امروز';
      case 'PreviousYear':
        return 'سال پیش';
    }
  }

  convertDataRangeFilter(controllerData: string, calenderType: string): string {
    const date = new Date(controllerData);
    const convertedDate = `${date.getFullYear()}/${date.getMonth() + 1}/${date.getDate()}`;
    if (calenderType === 'Shamsi') {
      const moment = require('jalali-moment');
      const momentDate = moment(convertedDate, 'YYYY/MM/DD');
      return momentDate.locale('fa').format('YYYY/M/D');
    }
    return convertedDate;
  }

  convertAdvancedQualityFilter(advancedQualityFilter: string,advancedQualityValue): string {
    switch (advancedQualityFilter) {
      case 'Include':
        return ' شامل ' + advancedQualityValue;
      case 'Exclude':
        return ' شامل ' + advancedQualityValue + ' نشود ';
      case 'StartWith':
        return ' شروع با ' + advancedQualityValue;
      case 'EqualTo':
        return ' مساوی ' + advancedQualityValue;
    }
    return 'null';
  }

  convertTopNQualityFilter(topNQualityFilter: string, value): string {
    switch (topNQualityFilter) {
      case 'Top':
        return ' بالاترین مقدار ' + value;
      case 'Bottom':
        return ' پایین‌ترین مقدار ' + value;
    }
    return 'null';
  }

  convertAdvancedQuantityFilter(advancedQualityFilter: string,advancedQuantityValue): string {
    switch (advancedQualityFilter) {
      case 'LessThan':
        return ' کمتر از ' + advancedQuantityValue;
      case 'LessEqual':
        return ' کمتر مساوی ' + advancedQuantityValue;
      case 'BiggerThan':
        return ' بزرگتر از ' + advancedQuantityValue;
      case 'BiggerEqual':
        return ' بزرگتر مساوی ' + advancedQuantityValue;
      case 'EqualTo':
        return ' مساوی ' + advancedQuantityValue + ' است ';
      case 'NotEqual':
        return ' مساوی ' + advancedQuantityValue + ' نیست ';
    }
    return 'null';
  }

  addControllersValuesToCharts(charts: Partial<ChartModel>[], isResetAll?: boolean): Partial<ChartModel>[] {
    let controllers = this.controllers$.getValue() ?? [];
    controllers = controllers.concat(this.getDefaultControllersValues(charts, isResetAll?[]:controllers));
    const drillThroughFilters = this.sourceDashboardControllers;
    if(Array.isArray(controllers)) {
      controllers.forEach((controller,index,selfControllers) => {
        const foundedController = charts.find(chart => chart.chartId === controller.controllerValue.controllerId);
        if(foundedController && !controller.controllerValue.selectedFieldFilter) {
          selfControllers[index].isInteractableWithCharts = foundedController.isInteractableWithCharts;
          selfControllers[index].otherChartsInteractionStatusWithThis =
            foundedController.otherChartsInteractionStatusWithThis;
        }
        charts.forEach((chart,i,selfCharts) => {
          selfCharts[i] = this.updateSingleChartControllersValues(chart,controller);
        });
      });
      this.controllers$.next([...controllers]);
    }
    if(Array.isArray(drillThroughFilters)) {
      drillThroughFilters.forEach((filter) => {
        filter.isInteractableWithCharts = false;
        filter.otherChartsInteractionStatusWithThis = [];
        charts.forEach((chart,index,selfCharts) => {
          selfCharts[index] = this.updateSingleChartControllersValues(chart,filter);
        });
      });
    }
    return [...charts];
  }

  getMapsList$(): Observable<Partial<MapModel[]>> {
    return this.mapsList$.asObservable();
  }

  updateMapsList(mapsList: Partial<MapModel[]>): void{
    this.mapsList$.next([...mapsList]);
  }

  addToMapsList(newMap: Partial<MapModel>): void {
    const currentMapsList = this.mapsList$.getValue();
    if(newMap) {
      currentMapsList.push(newMap as MapModel);
    }
    this.mapsList$.next([...currentMapsList]);
  }

  deleteMapFromList(map: Partial<MapModel>, chart: Partial<ChartModel>): void {
    const currentMapsList = this.mapsList$.getValue();
    const foundedIndex = currentMapsList.findIndex(mapTemp => mapTemp.mapId === map.mapId);
    if(foundedIndex > -1) {
      if(map.mapId === chart.mapId) {
        chart.mapId = null;
        chart.mapTopo = null;
        chart.mapFilePath = null;
        chart.hasError = true;
        this.updateOneChart(chart);
      }
      currentMapsList.splice(foundedIndex, 1);
      this.mapsList$.next([...currentMapsList]);
    }
  }

  getMapKeys$(): Observable<any> {
    return this.mapKeys$.asObservable().pipe(skip(1));
  }

  updateMapKeys(mapKeys: any): void {
    this.mapKeys$.next(mapKeys);
  }

  setDefaultChartsDrillDownValuesState(chartsLength: number) {
    this.chartsDrillDownValues$.next(Array(chartsLength).fill(undefined));
  }

  updateChartsDrillDownValues(chartIndex: number, drillDownValues: DrillingFilterValue[]): void {
    const newChartsDrillDownValues = this.chartsDrillDownValues$.getValue();
    if(newChartsDrillDownValues[chartIndex]) {
      newChartsDrillDownValues[chartIndex] = newChartsDrillDownValues[chartIndex].concat(drillDownValues);
    } else {
      newChartsDrillDownValues[chartIndex] = drillDownValues;
    }
    this.chartsDrillDownValues$.next([...newChartsDrillDownValues]);
  }

  chartDrillUp(chart: Partial<ChartModel>) {
    if(chart.drillingProperties) {
      if(chart.drillingProperties.drillingType[chart.drillingProperties.drillingType.length - 1] !== 'ChartDrillDown') {
        const updateChartsDrillDownValues = this.chartsDrillDownValues$.getValue();
        if(Array.isArray(updateChartsDrillDownValues[chart.chartIndexInDashboard]) &&
            updateChartsDrillDownValues[chart.chartIndexInDashboard].length > 0) {
          updateChartsDrillDownValues[chart.chartIndexInDashboard].pop();
        }
        this.chartsDrillDownValues$.next([...updateChartsDrillDownValues]);
      }
      chart.drillingProperties.drillLevel -= 1;
      chart.drillingProperties.drillingType.pop();
      chart.drillingProperties.drillDownRealColumns.pop();
      chart.drillingProperties.drillDownShowColumns.pop();
      this.updateOneChart(chart);
    }
  }

  updateChartDrillDownValueOnSpecificLevel(chart: Partial<ChartModel>, valueIndex: number): void {
    console.log(chart, valueIndex);
    const updateChartsDrillDownValues = this.chartsDrillDownValues$.getValue();
    for(let index = chart.drillingProperties.drillLevel - 2; index > valueIndex  ; index--) {
      if(chart.drillingProperties.drillingType[index] === 'ColumnDrillDown' &&
          Array.isArray(updateChartsDrillDownValues[chart.chartIndexInDashboard])) {
        updateChartsDrillDownValues[chart.chartIndexInDashboard].pop();
      }
      chart.drillingProperties.drillingType.pop();
      chart.drillingProperties.drillDownRealColumns.pop();
      chart.drillingProperties.drillDownShowColumns.pop();
    }
    if(valueIndex < chart.drillingProperties.drillLevel - 2) {
      this.chartsDrillDownValues$.next([...updateChartsDrillDownValues]);
      chart.drillingProperties.drillLevel = valueIndex + this.drillingStartAtLevel;
      this.updateOneChart(chart);
    }
  }

  removeChartDrillDownValues(chart: Partial<ChartModel>): void {
    const updateChartsDrillDownValues = this.chartsDrillDownValues$.getValue();
    if(Array.isArray(updateChartsDrillDownValues[chart.chartIndexInDashboard]) &&
        updateChartsDrillDownValues[chart.chartIndexInDashboard].length > 0) {
      updateChartsDrillDownValues[chart.chartIndexInDashboard] = undefined;
    }
    chart.drillingProperties = {
      drillLevel: 1,
      drillingType: ['None'],
      drillDownRealColumns: [],
      drillDownShowColumns: [],
    };
    this.chartsDrillDownValues$.next([...updateChartsDrillDownValues]);
    this.updateOneChart(chart);
  }

  updateDashboardShareLinkActivityStatus(isEnabled: boolean): void {
    const newShareProperties = this.dashboardShareProperties$.getValue();
    if(newShareProperties) {
      newShareProperties.isEnabled = isEnabled;
      this.updateDashboardShareProperties(newShareProperties);
    }
  }

  updateDashboardShareProperties(shareProperties: ShareProperties): void {
    this.dashboardShareProperties$.next({...shareProperties});
  }

  getDashboardShareProperties$(): Observable<ShareProperties> {
    return this.dashboardShareProperties$.asObservable().pipe(skip(1));
  }

  getValidDrillingFilterValues(chartIndexInDashboard: number): DrillingFilterValue[] {
    return this.chartsDrillDownValues$.getValue()[chartIndexInDashboard];
  }

  updateDashboardChartsNumberDisplayTypes(charts: Partial<ChartModel>[],
                                          numberDisplayType: NumberDisplayType): Partial<ChartModel>[] {
    if(!Array.isArray(charts)) {
      return [];
    }
    charts.forEach((chart,index,self) => {
      chart.numberDisplayType = numberDisplayType ?? 'fa';
      self[index] = {...chart};
    });
    return charts;
  }

  resetChartsHasUserSort(): void {
    this.chartsHasUserSort$.next([]);
  }

  getChartsHasUserSortValue(): Partial<ChartModel>[] {
    return this.chartsHasUserSort$.getValue();
  }

  updateChartsHasUserSort(chart: Partial<ChartModel>): void {
    const updateChartsHasUserSort = this.chartsHasUserSort$.getValue();
    const chartIndex = updateChartsHasUserSort.findIndex(chartTemp => chartTemp.chartId === chart.chartId);
    if(chartIndex > -1) {
      updateChartsHasUserSort.splice(chartIndex,1);
    }
    updateChartsHasUserSort.push(chart);
    this.chartsHasUserSort$.next([...updateChartsHasUserSort]);
  }

  clearChartsUserSort(): void {
    const updateChartsHasUserSort = this.chartsHasUserSort$.getValue();
    updateChartsHasUserSort.forEach((chart,index,self) => {
      chart.chartUserSort = undefined;
      self[index] = {...chart};
      this.updateOneChart(self[index]);
    });
    this.chartsHasUserSort$.next([...updateChartsHasUserSort]);
  }

  deleteChartUserSort(chart: Partial<ChartModel>): void {
    const updateChartsHasUserSort = this.chartsHasUserSort$.getValue();
    const chartIndex =updateChartsHasUserSort.findIndex(chartTemp => chartTemp.chartId === chart.chartId);
    if(chartIndex > -1) {
      updateChartsHasUserSort.splice(chartIndex,1);
    }
    this.chartsHasUserSort$.next([...updateChartsHasUserSort]);
    if(chart.chartUserSort !== undefined) {
      chart.chartUserSort = undefined;
      this.updateOneChart(chart);
    }
  }

  // user filter
  setDashboardUserFilters(userFilters: Partial<UserFilter>[]): void {
    this.dashboardUserFilters$.next(userFilters.map(filter => filter.userFilterId));
  }

  getDashboardUserFilters$(): Observable<string[]> {
    return this.dashboardUserFilters$.asObservable();
  }

  addUserFilterToDashboard(userFilter: Partial<UserFilter>) {
    this.dashboardUserFilters$.getValue().push(userFilter.userFilterId);
  }

  clearUserFilterFromDashboard() {
    this.dashboardUserFilters$.next([]);
  }

  removeUserFilterFromDashboard(userFilterId: string) {
    const updateUserFilters = this.dashboardUserFilters$.getValue();
    const foundedIndex = updateUserFilters.findIndex(filterId => filterId === userFilterId);
    if(foundedIndex > -1) {
      updateUserFilters.splice(foundedIndex, 1);
      this.dashboardUserFilters$.next([...updateUserFilters]);
    }
  }

  setUserFiltersListAddToDashboard(userFilters: Partial<UserFilter>[], selectedUserFilters: Partial<UserFilter>[]): void {
    this.userFiltersListAddToDashboard$.next([...selectedUserFilters, ...userFilters]);
  }

  getUserFiltersListAddToDashboard$(): Observable<Partial<UserFilter>[]> {
    return this.userFiltersListAddToDashboard$.asObservable();
  }

  setInitialChartsUserFilters(chartsLength: number): void {
    this.chartsUserFilters$.next(Array(chartsLength).fill(null));
    this.userFiltersListAddToCharts$.next(Array(chartsLength).fill(null));
  }

  getChartsUserFilters$(): Observable<string[][]> {
    return this.chartsUserFilters$.asObservable();
  }

  addNewChartUserFilters(): void {
    this.chartsUserFilters$.getValue().push(null);
    this.userFiltersListAddToCharts$.getValue().push(null);
  }

  removeChartUserFilters(chartIndex: number): void {
    const chartsUserFilters =  this.chartsUserFilters$.getValue();
    const userFiltersList = this.userFiltersListAddToCharts$.getValue();
    if(chartsUserFilters.length > chartIndex) {
      chartsUserFilters.splice(chartIndex,1);
      this.chartsUserFilters$.next(chartsUserFilters);
      this.selectedChartUserFilters$.next(null);
    }
    if (userFiltersList.length > chartIndex) {
      userFiltersList.splice(chartIndex,1);
      this.userFiltersListAddToCharts$.next(userFiltersList);
    }
  }

  setChartUserFilters(userFilters: Partial<UserFilter>[], chartIndex: number): void {
    const chartsUserFilters = this.chartsUserFilters$.getValue();
    if(chartsUserFilters.length > chartIndex) {
      chartsUserFilters[chartIndex] = userFilters.map(filter => filter.userFilterId);
      this.chartsUserFilters$.next(chartsUserFilters);
      this.selectedChartUserFilters$.next([...chartsUserFilters[chartIndex]]);
    }
  }

  updateChartUserFilters(userFilters: string[], chartIndex: number): void {
    const chartsUserFilters = this.chartsUserFilters$.getValue();
    if(chartsUserFilters.length > chartIndex) {
      chartsUserFilters[chartIndex] = userFilters?.length > -1 ? [...userFilters]: null;
      this.chartsUserFilters$.next(chartsUserFilters);
      this.selectedChartUserFilters$.next(chartsUserFilters[chartIndex]);
    }
  }

  getSelectedChartUserFilters$(): Observable<string[]> {
    return this.selectedChartUserFilters$.asObservable();
  }

  setUserFiltersListAddToChart(chart: Partial<ChartModel>, selectedUserFilters: Partial<UserFilter>[],
                               userFilters: Partial<UserFilter>[]): void {
    const userFiltersList = this.userFiltersListAddToCharts$.getValue();
    if(chart) {
      if(userFiltersList.length > chart.chartIndexInDashboard) {
        userFiltersList[chart.chartIndexInDashboard] = [...userFilters, ...selectedUserFilters];
        this.userFiltersListAddToCharts$.next([...userFiltersList]);
        this.selectedUserFiltersListAddToChart$.next(userFiltersList[chart.chartIndexInDashboard]);
      }
    }
  }

  getSelectedChartUserFiltersListAddToChart$(): Observable<Partial<UserFilter>[]> {
    return this.selectedUserFiltersListAddToChart$.asObservable();
  }

  addUserFilterToChart(userFilter: Partial<UserFilter>, chart: Partial<ChartModel>): void {
    const chartsUserFilters = this.chartsUserFilters$.getValue();
    if(chartsUserFilters.length > chart.chartIndexInDashboard) {
      if(chartsUserFilters[chart.chartIndexInDashboard] === null) {
        chartsUserFilters[chart.chartIndexInDashboard] = [userFilter.userFilterId];
      } else {
        chartsUserFilters[chart.chartIndexInDashboard].push(userFilter.userFilterId);
      }
      chartsUserFilters[chart.chartIndexInDashboard] = [...chartsUserFilters[chart.chartIndexInDashboard]];
      this.chartsUserFilters$.next(chartsUserFilters);
      this.selectedChartUserFilters$.next(chartsUserFilters[chart.chartIndexInDashboard]);
    }
  }

  removeUserFilterFromChart(userFilterId: string, chart: Partial<ChartModel>): void {
    const chartsUserFilters = this.chartsUserFilters$.getValue();
    if(chartsUserFilters.length > chart.chartIndexInDashboard && chartsUserFilters[chart.chartIndexInDashboard]) {
      const foundedIndex = chartsUserFilters[chart.chartIndexInDashboard]
          .findIndex(filterId => filterId === userFilterId);
      if(foundedIndex > -1) {
        chartsUserFilters[chart.chartIndexInDashboard].splice(foundedIndex, 1);
        chartsUserFilters[chart.chartIndexInDashboard] = [...chartsUserFilters[chart.chartIndexInDashboard]];
        this.chartsUserFilters$.next(chartsUserFilters);
        this.selectedChartUserFilters$.next([...chartsUserFilters[chart.chartIndexInDashboard]]);
      }
    }
  }

  setUserFilterDetails(userFilterDetails: UserFilterDetails): void {
    const users = [];
    if(userFilterDetails) {
      userFilterDetails.usersInUserFilter.forEach((tempUser, index) => {
        const selectedUser: Partial<UserInUserFilter> = {
          userFilterRecipientId: tempUser.userFilterRecipientId,
          userFilterRecipientType: tempUser.userFilterRecipientType,
          recipientName: tempUser.recipientName,
          isInclude: tempUser.isInclude,
          selectedValues: tempUser.selectedValues,
          index,
        };
        users.push(selectedUser);
      });
    }
    this.userFilterDetails$.next(userFilterDetails);
    this.selectedUsersInUserFilter$.next(users);
  }

  getUserFilterDetails$(): Observable<UserFilterDetails> {
    return this.userFilterDetails$.asObservable();
  }

  getSelectedUsersInUserFilter$(): Observable<Partial<UserInUserFilter>[]> {
    return this.selectedUsersInUserFilter$.asObservable();
  }

  setDashboardUserFilterIsPermitted(isPermitted: boolean): void {
    this.dashboardUserFilterIsPermitted$.next(isPermitted);
  }

  getDashboardUserFilterIsPermitted$(): Observable<boolean> {
    return this.dashboardUserFilterIsPermitted$.asObservable();
  }

  setMinMaxColumnData(dateFilter: string[]): void {
    this.minMaxColumnData$.next(dateFilter);
  }

  getMinMaxColumnData$(): Observable<string[]> {
    return this.minMaxColumnData$.asObservable();
  }

  updateCompactedDashboardListStatus(compacted: boolean): void {
      this.compactedDashboardListStatus$.next(compacted);
  }

  getCompactedDashboardListStatus$(): Observable<boolean> {
    return this.compactedDashboardListStatus$.asObservable();
  }

  updateDashboardListResizeStatus(compacted: boolean): void {
      this.dashboardsListResize$.next(compacted);
  }

  getDashboardListResizeStatus$(): Observable<boolean> {
    return this.dashboardsListResize$.asObservable();
  }
  setInitialChartsZoomStatus(chartsLength: number): void {
    this.chartsZoomStatus$.next(Array(chartsLength).fill(false));
  }

  updateChartZoomStatus(chartIndex: number, zoomStatus: boolean = undefined): void {
    const updateChartsZoomStatus = this.chartsZoomStatus$.getValue();
    if(updateChartsZoomStatus.length > chartIndex) {
      updateChartsZoomStatus[chartIndex] = zoomStatus == null ? !updateChartsZoomStatus[chartIndex] : zoomStatus;
      this.chartsZoomStatus$.next([...updateChartsZoomStatus]);
    }
  }

  getChartsZoomStatus$(): Observable<boolean[]> {
    return this.chartsZoomStatus$.asObservable();
  }

  resetChartsZoomStatus(charts: Partial<ChartModel>[]) {
    const updateChartsZoomStatus = this.chartsZoomStatus$.getValue();
    let chartsZoomStatusChanged = false;
    if(Array.isArray(charts)) {
      charts.forEach(chart => {
        if(updateChartsZoomStatus[chart.chartIndexInDashboard]) {
          updateChartsZoomStatus[chart.chartIndexInDashboard] = false;
          chartsZoomStatusChanged = true;
        }
      });
    }
    if(chartsZoomStatusChanged) {
      this.chartsZoomStatus$.next([...updateChartsZoomStatus]);
    }
  }
  setDefaultDataSource(dataSource: Partial<Datasource>): void {
    this.defaultDataSource$.next(dataSource);
  }

  getDefaultDataSource$(): Observable<Partial<Datasource>> {
    return this.defaultDataSource$.asObservable();
  }
  setDefaultDataSourceDate(dataSource: number): void {
    this.defaultDataSourceDate$.next(dataSource);
  }

  getDefaultDataSourceDate$(): Observable<number> {
    return this.defaultDataSourceDate$.asObservable();
  }

  getDefaultDataSourceValue(): Partial<Datasource> {
    return this.defaultDataSource$.getValue();
  }

  updateDashboardInfo(info: DashboardInfoDto): void {
    this.dashboardInfo$.next(info);
  }

  getDashboardInfo$(): Observable<DashboardInfoDto | null> {
    return this.dashboardInfo$.asObservable();
  }
  setChartInsight(insight: any): void {
    this.chartInsight$.next(insight);
  }

  getChartInsight$(): Observable<any> {
    return this.chartInsight$.asObservable();
  }

  getChartInsightValue(): any {
    return this.chartInsight$.getValue();
  }
  updateChartInsight(insight: { chartId: string; isGenerating: boolean; text: any; statusCode: number | null }): void {
    const current = this.getChartInsightValue() || {};
    this.setChartInsight({
      ...current,
      [insight.chartId]: {
        isGenerating: insight.isGenerating,
        text: insight.text,
        statusCode: insight.statusCode
      }
    });
  }
  setDashboardHistoryCurrent(item: any): void { this.dashboardHistoryCurrent.set(item); }
  setDashboardHistoryList(list: any[]): void { this.dashboardHistoryList.set(list); }
  setDashboardHistoryLoading(isLoading: boolean): void { this.dashboardHistoryLoading.set(isLoading); }
  setDashboardRecoveryCurrent(item: any): void { this.dashboardRecoveryCurrent.set(item); }
  setDashboardRecoveryLoading(isLoading: boolean): void { this.dashboardRecoveryLoading.set(isLoading); }
  setDashboardRecoverySuccess(item: any): void { this.dashboardRecoverySuccess.set(item); }
  setDashboardPinLoading(isLoading: boolean): void { this.dashboardPinLoading.set(isLoading); }
  setDashboardPinCurrent(item: any): void { this.dashboardPinCurrent.set(item); }
  toggleDashboardInHistoryMode(isInHistoryMode: boolean): void { this.isDashboardInHistoryMode.set(isInHistoryMode) }



  setChartDatasourceState(chartId: string, partial: Partial<{
    list: any[];
    selectedId: string | null;
    hasDefault: boolean;
    loading: boolean;
    tickTime: string | null;
  }>): void {
    const current = this.defaultDatasourceByChart();
    this.defaultDatasourceByChart.set({
      ...current,
      [chartId]: {
        ...(current[chartId] ?? {
          list: [],
          selectedId: null,
          hasDefault: false,
          loading: false,
          tickTime: null
        }),
        ...partial
      }
    });
  }

  getChartDatasourceState(chartId: string): {
    list: any[];
    selectedId: string | null;
    hasDefault: boolean;
    loading: boolean;
    tickTime: string | null;
  } | undefined {
    return this.defaultDatasourceByChart()[chartId];
  }


  setChartNameDuplicated(value: boolean): void {
    this.chartNameDuplicated.set(value);
  }
  setDashboardNameDuplicated(value: boolean) {
    this.dashboardNameDuplicated.set(value);
  }
  setNameSubmitState(value: SubmitType) {
    this.nameSubmitState.set(value);
  }



  //import export states 
  setImportInitialData(importDashboardId: string, datasources: ImportDatasourceState[]) {
    this.importDashboardId.set(importDashboardId);
    this.importDatasources.set(datasources);

    const firstFailedIndex = datasources.findIndex(x => !x.status);
    this.importStepIndex.set(firstFailedIndex === -1 ? 0 : firstFailedIndex);
  }

  setImportSelectedDatasource(id: string, name: string) {
    const index = this.importStepIndex();
    const list = [...this.importDatasources()];
    list[index] = { ...list[index], selectedDatasourceId: id, selectedDataSourceName: name };
    this.importDatasources.set(list);
  }

  setImportDatasourceResult(
    isSuccess: boolean,
    errors: string[],
    selectedId: string,
    selectedName: string
  ) {
    const index = this.importStepIndex();
    const list = [...this.importDatasources()];
  
    list[index] = {
      ...list[index],
      status: isSuccess,
      errors: errors ?? [],
      selectedDatasourceId: selectedId,
      selectedDataSourceName: selectedName,
    };
  
    this.importDatasources.set(list);
  }
  

  nextImportStep() {
    this.importStepIndex.set(this.importStepIndex() + 1);
  }

resetCurrentImportDatasourceSelection(
  selectedId: string | null,
  selectedName: string | null
) {
  const index = this.importStepIndex();
  const list = [...this.importDatasources()];

  list[index] = {
    ...list[index],
    status: null,
    errors: [],
    selectedDatasourceId: selectedId,
    selectedDataSourceName: selectedName,
  };

  this.importDatasources.set(list);
}


  resetImportState() {
    this.importDashboardId.set(null);
    this.importDatasources.set([]);
    this.importStepIndex.set(0);
  }
  
  moveToFirstUnresolvedStep(fromIndex?: number) {
    const list = this.importDatasources();
    const startIndex = fromIndex ?? this.importStepIndex();
  
    const nextUnresolvedIndex = list
      .slice(startIndex)
      .findIndex(ds => ds.status !== true);
  
    if (nextUnresolvedIndex === -1) {
      this.importStepIndex.set(list.length);
    } else {
      this.importStepIndex.set(startIndex + nextUnresolvedIndex);
    }
  }
    
}
